export type Maybe<T> = T | null

export interface IPaginationInput {
  pageTake: number

  pageSkip: number
}

export interface IOrderInput {
  field: string

  fieldType?: Maybe<FieldType>

  direction?: Maybe<OrderDirection>

  relatedRiskTemplateId?: Maybe<string>

  customColumnSources?: Maybe<CustomColumnSource[]>
}

export interface IAiRecordReference {
  riskId: string
  templateId: string
  title: Maybe<string>
}

export interface IAiSuggestionField {
  fieldId: string
  value: any
}

export interface IAiRiskSuggestionField {
  fieldId: string
  optionId: Maybe<string>
  value: Maybe<string>
}

export interface IAiRiskSuggestion {
  title: string
  fields: IAiRiskSuggestionField[]
}

export interface IAiMatchedFilter {
  id: string
  name: string
}

export interface IAiSuggestedRecord {
  existingRecordId?: string
  matchedFilter?: IAiMatchedFilter
  fields: IAiSuggestionField[]
}

export interface IAiAssistResponse {
  suggestions: IAiSuggestedRecord[]
  fieldLinkId: string
  templateId: string
}

export interface IAiRiskAssessmentInput {
  riskId: string
  fieldId: string
}

export interface IAiRiskAssessmentResponse {
  fieldId: string
  value: any
  explanation: string
}

export interface IAiMessage {
  content: Maybe<Maybe<string>>
  createdAt: Maybe<Maybe<string>>
  id: string
  role: Maybe<Maybe<string>>
  records?: Maybe<IAiRecordReference[]>
  riskSuggestions?: Maybe<IAiRiskSuggestion[]>
}

export interface IAiConversation {
  createdAt: Maybe<Maybe<string>>
  id: Maybe<Maybe<string>>
  riskViewId: Maybe<Maybe<string>>
  riskId: Maybe<Maybe<string>>
  title: Maybe<Maybe<string>>
  updatedAt: Maybe<Maybe<string>>
  userId: Maybe<Maybe<string>>
}

export interface IAiConversationFilterInput {
  riskViewId?: Maybe<string>

  riskId?: Maybe<string>
}

export interface IAiMessageInput {
  riskViewId?: Maybe<string>

  riskId?: Maybe<string>

  message: string

  conversationId?: Maybe<string>
}

export interface IAiRecordSummaryInput {
  riskId: string
}

export interface IAiRecordSummaryResponse {
  conversationId: Maybe<Maybe<string>>
  summary: Maybe<Maybe<string>>
}

export interface IAiUsageStats {
  totalCost: Maybe<Maybe<number>>

  totalConversations: Maybe<Maybe<number>>
}

export interface IBusinessActivityFilterInput {
  ids?: Maybe<Maybe<string>[]>

  description?: Maybe<string>
}

export interface IBusinessProcessFilterInput {
  ids?: Maybe<Maybe<string>[]>

  description?: Maybe<string>
}

export interface IBusinessUnitFilterInput {
  ids?: Maybe<Maybe<string>[]>

  description?: Maybe<string>
}

export interface ICompanyFilterInput {
  name?: Maybe<string>

  id?: Maybe<string>
}

export interface ICompanyFieldFilterInput {
  name?: Maybe<string>
}

export interface IDashboardItemsInput {
  dashboardId: string
}

export interface IFilterFilterInput {
  target?: Maybe<FilterTarget>

  name?: Maybe<string>

  id?: Maybe<string>

  targetId?: Maybe<string>
}

export interface IGlobalSearchFilterInput {
  search?: Maybe<string>

  showPlans?: Maybe<boolean>

  showKPIs?: Maybe<boolean>

  showActions?: Maybe<boolean>

  showObjectives?: Maybe<boolean>
}

export interface IPlanItemFilterInput {
  ids?: Maybe<Maybe<string>[]>

  referenced?: Maybe<boolean>

  planId?: Maybe<string>

  archived?: Maybe<boolean>
}

export interface ILinkingItemFilter {
  ids?: Maybe<Maybe<string>[]>
}

export interface ILocationFilterInput {
  ids?: Maybe<Maybe<string>[]>

  description?: Maybe<string>
}

export interface ILogFilterInput {
  id?: Maybe<string>

  entityId?: Maybe<string>

  metadataEntityId?: Maybe<string>

  entityType?: Maybe<Maybe<LogEntityTypes>[]>

  actionType?: Maybe<Maybe<LogActionTypes>[]>
}

export interface IObjectEntityFilterInput {
  id?: Maybe<string>

  apiName?: Maybe<string>
}

export interface IObjectSecurityFilter {
  companyId?: Maybe<string>

  name?: Maybe<string>

  objectEntityId?: Maybe<string>

  objectEntityApiName?: Maybe<string>
}

export interface IPlanFilterInput {
  id?: Maybe<string>

  ids?: Maybe<Maybe<string>[]>

  filterSlug?: Maybe<string>

  name?: Maybe<string>

  planTypeIds?: Maybe<Maybe<string>[]>

  ownerIds?: Maybe<Maybe<string>[]>

  statuses?: Maybe<Maybe<string>[]>
}

export interface IPlanActionFilterInput {
  ids?: Maybe<Maybe<string>[]>

  planId?: Maybe<string>

  description?: Maybe<string>

  ownedByIds?: Maybe<Maybe<string>[]>

  statuses?: Maybe<Maybe<string>[]>

  position?: Maybe<number>

  filterSlug?: Maybe<string>
}

export interface IPlanKpiFilterInput {
  ids?: Maybe<Maybe<string>[]>

  planId?: Maybe<string>

  description?: Maybe<string>

  position?: Maybe<number>

  ownedByIds?: Maybe<Maybe<string>[]>

  statuses?: Maybe<Maybe<string>[]>

  filterSlug?: Maybe<string>
}

export interface IPlanObjectiveFilterInput {
  ids?: Maybe<Maybe<string>[]>

  planId?: Maybe<string>

  ownedByIds?: Maybe<Maybe<string>[]>

  description?: Maybe<string>

  position?: Maybe<number>

  filterSlug?: Maybe<string>
}

export interface IPlanResourcesFilterInput {
  id?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>
}

export interface IPlanTypeFilterInput {
  ids?: Maybe<Maybe<string>[]>

  description?: Maybe<string>
}

export interface IPositionFilterInput {
  ids?: Maybe<Maybe<string>[]>

  companyId?: Maybe<string>

  description?: Maybe<string>

  businessUnitsIds?: Maybe<string[]>
}

export interface IPowerBiReportsFilterInput {
  ids?: Maybe<Maybe<string>[]>
}

export interface IPowerBiCheckExportToFileStatusInput {
  exportId: string

  reportId: string
}

export interface IRiskFilterInput {
  ids?: Maybe<Maybe<string>[]>

  recurrence?: Maybe<boolean>

  fieldFilters?: Maybe<Maybe<IFieldFilter>[]>

  riskTemplateId?: Maybe<string>

  parentRiskId?: Maybe<string>
}

export interface IFieldFilter {
  id?: Maybe<string>

  fieldType?: Maybe<FieldType>

  riskTemplateId?: Maybe<string>

  systemName?: Maybe<string>

  value?: Maybe<Json>

  action?: Maybe<SystemProcessConditionAction>

  fromCustomColumn?: Maybe<boolean>
}

export interface IRiskTemplateFilterInput {
  ids?: Maybe<Maybe<string>[]>

  name?: Maybe<string>
}

export interface IRiskViewFilterInput {
  ids?: Maybe<string[]>

  riskTemplateIds?: Maybe<string[]>
}

export interface ISecurityGroupFilterInput {
  companyId?: Maybe<string>

  name?: Maybe<string>
}

export interface ISecurityProfileFilterInput {
  companyId?: Maybe<string>

  name?: Maybe<string>
}

export interface ISystemProcessFilter {
  search?: Maybe<string>

  objects?: Maybe<Maybe<SystemProcessObject>[]>

  objectId?: Maybe<string>
}

export interface IUserFilter {
  name?: Maybe<string>

  search?: Maybe<string>

  companyId?: Maybe<string>

  id?: Maybe<string>

  excludeMyTeam?: Maybe<boolean>

  ids?: Maybe<Maybe<string>[]>

  businessUnitsIds?: Maybe<string[]>
}

export interface IUsersOrPositionsFilter {
  ids?: Maybe<string[]>

  companyId?: Maybe<string>

  search?: Maybe<string>

  excludeMyTeam?: Maybe<boolean>

  onlyMyTeam?: Maybe<boolean>
}

export interface IFileInput {
  assetType: AssetType

  contentType: string

  entityPrefix: string

  originalFileName?: string
}

export interface IBookmarkCreateInput {
  planId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  planObjectiveId?: Maybe<string>
}

export interface IBusinessActivityUpdateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>
}

export interface IBusinessActivityCreateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  position?: Maybe<number>
}

export interface IBusinessActivitiesTreeUpdateInput {
  id: string

  parentId?: Maybe<string>

  expanded: boolean

  position?: Maybe<number>
}

export interface IBusinessProcessUpdateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>
}

export interface IBusinessProcessCreateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  position?: Maybe<number>
}

export interface IBusinessProcessesTreeUpdateInput {
  id: string

  parentId?: Maybe<string>

  expanded: boolean

  position?: Maybe<number>
}

export interface IBusinessUnitUpdateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>
}

export interface IBusinessUnitCreateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  position?: Maybe<number>
}

export interface IBusinessUnitsTreeUpdateInput {
  id: string

  parentId?: Maybe<string>

  expanded: boolean

  position?: Maybe<number>
}

export interface ICompanyCreateInput {
  name: string

  code: string

  address?: Maybe<string>

  domainName: string

  language: string

  dateAndNumberFormat: string

  financialYearStartsAt: DateString

  financialYearFinishesAt: DateString

  sector: string[]

  timezone: string

  businessDays: IBusinessDayInput[]

  legitimateInterest?: Maybe<string>

  privacyPolicy?: Maybe<string>

  consentToCommunicate?: Maybe<string>

  licences: ILicenceInput[]

  currency?: Maybe<string>

  businessNumber?: Maybe<string>

  companyNumber?: Maybe<string>

  logoUrl?: Maybe<string>

  logoUrlFilename?: Maybe<string>
}

export interface IBusinessDayInput {
  key: string

  startTime: string

  finishTime: string
}

export interface ILicenceInput {
  active: boolean

  name: string

  type: string

  standartCount?: Maybe<string>

  professionalCount?: Maybe<string>

  subscriptionLevel?: Maybe<CompanySubscriptionLevel>
}

export interface ICompanyUpdateInput {
  name?: Maybe<string>

  code?: Maybe<string>

  domainName?: Maybe<string>

  language?: Maybe<string>

  dateAndNumberFormat?: Maybe<string>

  financialYearStartsAt?: Maybe<DateString>

  financialYearFinishesAt?: Maybe<DateString>

  sector?: Maybe<Maybe<string>[]>

  address?: Maybe<string>

  timezone?: Maybe<string>

  businessDays?: Maybe<Maybe<IBusinessDayInput>[]>

  legitimateInterest?: Maybe<string>

  privacyPolicy?: Maybe<string>

  consentToCommunicate?: Maybe<string>

  licences?: Maybe<Maybe<ILicenceInput>[]>

  currency?: Maybe<string>

  businessNumber?: Maybe<string>

  companyNumber?: Maybe<string>

  logoUrl?: Maybe<string>

  logoUrlFilename?: Maybe<string>
}

export interface ICompanyUpdateRiskResponseTypesInput {
  responseTypesSets: IResponseTypesSetInput[]
}

export interface IResponseTypesSetInput {
  id: string

  options: IRiskResponseTypeOptionInput[]
}

export interface IRiskResponseTypeOptionInput {
  id: string

  label?: Maybe<string>

  value?: Maybe<string>

  score?: Maybe<string>

  active?: Maybe<boolean>

  color?: Maybe<string>
}

export interface IDashboardCreateInput {
  active: boolean

  title: string

  description?: Maybe<string>

  availableTo: IDashboardAvailableToInput
}

export interface IDashboardAvailableToInput {
  availableToUserIds: string[]
}

export interface IDashboardUpdateInput {
  id: string

  active?: Maybe<boolean>

  title?: Maybe<string>

  description?: Maybe<string>

  availableTo?: Maybe<IDashboardAvailableToInput>
}

export interface IDashboardDeleteInput {
  id: string
}

export interface IDashboardItemCreateInput {
  dashboardId?: Maybe<string>

  parentDashboardItemId?: Maybe<string>

  position?: Maybe<number>

  itemType?: Maybe<DashboardItemType>

  settings?: Maybe<IDashboardItemSettingsInput>
}

export interface IDashboardItemSettingsInput {
  title?: Maybe<string>

  description?: Maybe<string>

  width?: Maybe<number>

  height?: Maybe<number>

  padding?: Maybe<number>

  transparency?: Maybe<number>

  showTitle?: Maybe<boolean>

  dropShadow?: Maybe<boolean>

  openInWindow?: Maybe<boolean>

  imageUrl?: Maybe<string>

  imageFilename?: Maybe<string>

  riskTemplateId?: Maybe<string>

  riskViewId?: Maybe<string>

  filterId?: Maybe<string>

  functionType?: Maybe<DashboardItemFunctionType>

  functionLink?: Maybe<string>
}

export interface IDashboardItemUpdateInput {
  id: string

  dashboardId?: Maybe<string>

  parentDashboardItemId?: Maybe<string>

  position?: Maybe<number>

  itemType?: Maybe<DashboardItemType>

  settings?: Maybe<IDashboardItemSettingsInput>
}

export interface IDashboardItemDeleteInput {
  id: string
}

export interface IFieldInput {
  objectEntityId?: Maybe<string>

  entityId?: Maybe<string>

  parentFieldId?: Maybe<string>

  readonly?: Maybe<boolean>

  hidden?: Maybe<boolean>

  standard?: Maybe<boolean>

  restricted?: Maybe<boolean>

  fieldType?: Maybe<FieldType>

  label?: Maybe<string>

  active?: Maybe<boolean>

  searchable?: Maybe<boolean>

  searchTemplateIds?: Maybe<string[]>

  wysiwyg?: Maybe<boolean>

  options?: Maybe<Maybe<IFieldOptionInput>[]>

  format?: Maybe<Maybe<IFieldOptionInput>[]>

  source?: Maybe<Maybe<FieldSource>[]>

  multiline?: Maybe<boolean>

  multiple?: Maybe<boolean>

  required?: Maybe<boolean>

  displayEmail?: Maybe<boolean>

  hideName?: Maybe<boolean>

  includeHistoryInApiResponse?: Maybe<boolean>

  exportHeaders?: Maybe<boolean>

  responseType?: Maybe<boolean>

  responseTypeId?: Maybe<string>

  weight?: Maybe<number>

  position?: Maybe<number>

  historyEnabled?: Maybe<boolean>

  hideOnPublic?: Maybe<boolean>

  isPublicOnly?: Maybe<boolean>

  showUserDetails?: Maybe<boolean>

  visibleUserDetailsFields?: Maybe<string[]>

  includeInAiContext?: Maybe<boolean>

  aiAutoPopulate?: Maybe<boolean>

  aiIncludeHistory?: Maybe<boolean>

  aiContextDescription?: Maybe<string>

  aiSuggestedPrompts?: Maybe<string[]>

  settings?: Maybe<Json>

  objectSecurityId?: Maybe<string>

  backgroundColor?: Maybe<string>

  limitResponse?: Maybe<Json>
}

export interface IFieldOptionInput {
  id?: Maybe<string>

  label?: Maybe<string>

  value?: Maybe<string>

  active?: Maybe<boolean>
}

export interface IFieldUpdatePositionInput {
  id?: Maybe<string>

  position?: Maybe<number>
}

export interface IFieldGroupInput {
  entityId: string

  fieldId: string

  parentFieldId?: Maybe<string>

  groupedByEntityId?: Maybe<string>
}

export interface IFieldGroupBunchDeleteInput {
  id: string
}

export interface IFieldLinkSettings {
  copyRecords: Maybe<boolean>

  createNewRecords: Maybe<boolean>

  referenceRecords: Maybe<boolean>

  excludeFieldsWhenCopying: Maybe<string[]>
}

export interface IFieldLinkInput {
  id?: Maybe<string>

  fieldId: string

  targetEntityId: string

  addFieldLabel?: Maybe<string>

  riskViewId?: Maybe<string>

  fieldLabel?: Maybe<string>

  parentFieldId?: Maybe<string>

  position?: Maybe<number>

  primary?: Maybe<boolean>

  fieldGroupIds?: Maybe<Maybe<string>[]>

  linkingSettings?: Maybe<IFieldLinkSettings>
}

export interface IFieldLinkBunchDeleteInput {
  id: string
}

export interface IFieldValueInput {
  fieldId?: Maybe<string>

  fieldLinkId?: Maybe<string>

  value?: Maybe<Json>

  row?: Maybe<number>

  column?: Maybe<number>

  fieldValueId?: Maybe<string>

  parentFieldValueId?: Maybe<string>

  createdAt?: Maybe<DateString>
}

export interface IFilterCreateInput {
  name?: Maybe<string>

  args?: Maybe<Json>

  meta?: Maybe<Json>

  target?: Maybe<FilterTarget>

  targetId?: Maybe<string>
}

export interface IFilterUpdateInput {
  id: string

  name?: Maybe<string>

  args?: Maybe<Json>

  meta?: Maybe<Json>

  target?: Maybe<FilterTarget>

  targetId?: Maybe<string>
}

export interface IFilterDeleteInput {
  id: string
}

export interface IActiveDirectoryDeleteInput {
  tokenId: string
}

export interface ITokenUpdateInput {
  id: string

  mappings: ITokenMappingInput[]
}

export interface ITokenMappingInput {
  id: string

  conditions: ISystemProcessConditionInput[]

  transactions: ISystemProcessTransactionInput[]
}

export interface ISystemProcessConditionInput {
  conditionType?: Maybe<SystemProcessConditionType>

  fieldName?: Maybe<string>

  fieldLinkId?: Maybe<string>

  entityName?: Maybe<SystemProcessObject>

  action?: Maybe<SystemProcessConditionAction>

  fieldType?: Maybe<FieldType>

  value?: Maybe<Json>

  inlineFunctionId?: Maybe<string>

  conditions?: Maybe<Maybe<ISystemProcessConditionInput>[]>
}

export interface ISystemProcessTransactionInput {
  id?: Maybe<string>

  transactionType?: Maybe<SystemProcessTransactionType>

  recipients?: Maybe<Maybe<ISystemProcessTransactionRecipientInput>[]>

  fieldName?: Maybe<string>

  fieldLinkId?: Maybe<string>

  inlineFunctionId?: Maybe<string>

  fieldValue?: Maybe<Json>

  subject?: Maybe<string>

  template?: Maybe<string>

  functionName?: Maybe<string>

  availableToFieldIds?: Maybe<string[]>

  availableToUserIds?: Maybe<string[]>

  availableToPositionIds?: Maybe<string[]>

  relatedForms?: Maybe<ISystemProcessRelatedFormInput>

  riskRecurrence?: Maybe<ISystemProcessTransactionRiskRecurrenceInput>

  disableRiskRecurrence?: Maybe<ISystemProcessTransactionDisableRiskRecurrenceInput>

  enableRiskRecurrence?: Maybe<ISystemProcessTransactionEnableRiskRecurrenceInput>

  supervisorHierarchy?: Maybe<ISystemProcessTransactionSupervisorHierarchyInput>

  restrictFieldId?: Maybe<string>

  groupsSecurityAccess?: Maybe<Maybe<IGroupsSecurityAccessInput>[]>

  position?: Maybe<number>
}

export interface ISystemProcessTransactionRecipientInput {
  sendTo?: Maybe<SystemProcessTransactionSendTo>

  sendToUsers?: Maybe<Maybe<string>[]>

  sendToPositions?: Maybe<Maybe<string>[]>

  sendToFieldIds?: Maybe<Maybe<string>[]>

  sendToPreviousFieldIds?: Maybe<Maybe<string>[]>

  sendToParentReportsToPersonByFieldIds?: Maybe<Maybe<string>[]>

  sendToParentReportsToPositionByFieldIds?: Maybe<Maybe<string>[]>

  sendToParentPreviousFieldIds?: Maybe<Maybe<string>[]>

  sendToParentFieldIds?: Maybe<Maybe<string>[]>

  sendToReportsToPersonByFieldIds?: Maybe<Maybe<string>[]>

  sendToReportsToPositionByFieldIds?: Maybe<Maybe<string>[]>
}

export interface ISystemProcessRelatedFormInput {
  riskTemplateId?: Maybe<string>

  multiple?: Maybe<boolean>

  position?: Maybe<number>

  inheritToManagers?: Maybe<boolean>

  businessRules?: Maybe<Maybe<ISystemProcessBusinessRuleInput>[]>
}

export interface ISystemProcessBusinessRuleInput {
  userOrPositionFieldId?: Maybe<string>

  securityProfileId?: Maybe<string>

  userOrPositionId?: Maybe<string>

  inheritSecurityProfileId?: Maybe<boolean>
}

export interface ISystemProcessTransactionRiskRecurrenceInput {
  riskTemplateId: string

  recurrence?: Maybe<Json>

  fieldValues?: Maybe<IFieldValueInput[]>
}

export interface ISystemProcessTransactionDisableRiskRecurrenceInput {
  riskTemplateId: string
}

export interface ISystemProcessTransactionEnableRiskRecurrenceInput {
  riskTemplateId: string
}

export interface ISystemProcessTransactionSupervisorHierarchyInput {
  fieldIds: string[]
}

export interface IGroupsSecurityAccessInput {
  userId?: Maybe<string>

  positionId?: Maybe<string>

  securityProfileId?: Maybe<string>
}

export interface ILinkingItemCreateInput {
  sourcePlanKPIId?: Maybe<string>

  destinationPlanKPIId?: Maybe<string>

  sourcePlanActionId?: Maybe<string>

  destinationPlanActionId?: Maybe<string>

  sourcePlanObjectiveId?: Maybe<string>

  destinationPlanObjectiveId?: Maybe<string>

  description?: Maybe<string>

  parentId?: Maybe<string>

  weighting?: Maybe<number>
}

export interface ILinkingItemUpdateInput {
  description?: Maybe<string>

  parentId?: Maybe<string>

  weighting?: Maybe<number>
}

export interface ILocationUpdateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  geoLocation?: Maybe<IGeoLocationInput>
}

export interface IGeoLocationInput {
  city?: Maybe<string>

  state?: Maybe<string>

  address?: Maybe<string>

  country?: Maybe<string>

  latitude?: Maybe<number>

  longitude?: Maybe<number>
}

export interface ILocationCreateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  position?: Maybe<number>

  geoLocation?: Maybe<IGeoLocationInput>
}

export interface ILocationsTreeUpdateInput {
  id: string

  parentId?: Maybe<string>

  expanded: boolean

  position?: Maybe<number>
}

export interface ILogCreateInput {
  entityId?: Maybe<string>

  entityType: LogEntityTypes

  actionType: LogActionTypes

  metadata?: Maybe<ILogCreateInputMetadata>
}

export interface ILogCreateInputMetadata {
  fileName?: Maybe<string>

  fileUrl?: Maybe<string>

  fileContentType?: Maybe<string>

  comment?: Maybe<string>
}

export interface ILogUpdateInput {
  entityType?: Maybe<LogEntityTypes>

  actionType?: Maybe<LogActionTypes>

  metadata?: Maybe<ILogCreateInputMetadata>
}

export interface IObjectEntityUpdateInput {
  memberObjectSecurityId?: Maybe<string>

  ownerObjectSecurityId?: Maybe<string>

  reviewQuestions?: Maybe<IObjectEntityReviewQuestionInput[]>

  reviewFieldIds?: Maybe<string[]>
}

export interface IObjectEntityReviewQuestionInput {
  id?: Maybe<string>

  position?: Maybe<number>

  text?: Maybe<string>
}

export interface IObjectSecurityInput {
  name?: Maybe<string>

  description?: Maybe<string>

  active?: Maybe<boolean>

  objectEntityId?: Maybe<string>

  objectEntityAPIName?: Maybe<string>

  securityProfileIds?: Maybe<Maybe<string>[]>
}

export interface IPlanUpdateTreeInput {
  id?: Maybe<string>

  expanded?: Maybe<boolean>

  treePosition?: Maybe<number>

  parentId?: Maybe<string>
}

export interface IPlanCreateInput {
  private?: Maybe<boolean>

  planTypeId: string

  status?: Maybe<string>

  name: string

  ownedById?: Maybe<string>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>
  /** referencing & linking */
  parentPlanId?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanUpdateInput {
  private?: Maybe<boolean>

  locked?: Maybe<boolean>

  planTypeId?: Maybe<string>

  status?: Maybe<string>

  name?: Maybe<string>

  ownedById?: Maybe<string>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>
  /** referencing & linking */
  parentPlanId?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanDeleteInput {
  force: boolean
}

export interface IPlanActionCreateInput {
  description: string

  milestone?: Maybe<boolean>

  ownedById?: Maybe<string>

  commencementAt?: Maybe<DateString>

  completionAt?: Maybe<DateString>

  completionPercentage?: Maybe<number>

  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanActionUpdateInput {
  milestone?: Maybe<boolean>

  description?: Maybe<string>

  ownedById?: Maybe<string>

  commencementAt?: Maybe<DateString>

  completionAt?: Maybe<DateString>

  completionPercentage?: Maybe<number>

  treePosition?: Maybe<number>

  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanItemCreateInput {
  parentId?: Maybe<string>

  treePosition?: Maybe<number>

  planId: string
  /** for referencing */
  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  referenced?: Maybe<boolean>

  planKPIInput?: Maybe<IPlanKpiCreateInput>

  planObjectiveInput?: Maybe<IPlanObjectiveCreateInput>

  planActionInput?: Maybe<IPlanActionCreateInput>
}

export interface IPlanKpiCreateInput {
  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  inputMethod?: Maybe<InputMethod>

  ownedById?: Maybe<string>

  description?: Maybe<string>

  details?: Maybe<string>

  unit?: Maybe<string>

  resultType?: Maybe<string>

  resultTarget?: Maybe<Json>

  recurrence?: Maybe<Json>

  resultTypeValue?: Maybe<string>

  resultBaseline?: Maybe<string>

  resultBaselineScale?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>

  resultTypeScale?: Maybe<Maybe<IPlanKpiScaleOptionCudInput>[]>
}

export interface IPlanKpiScaleOptionCudInput {
  id?: Maybe<string>

  label?: Maybe<string>

  value?: Maybe<string>

  active?: Maybe<boolean>

  position?: Maybe<number>
}

export interface IPlanObjectiveCreateInput {
  description: string

  typeOptionId?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanKpiUpdateInput {
  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>

  businessUnitIds?: Maybe<Maybe<string>[]>

  businessProcessIds?: Maybe<Maybe<string>[]>

  businessActivityIds?: Maybe<Maybe<string>[]>

  inputMethod?: Maybe<InputMethod>

  ownedById?: Maybe<string>

  description?: Maybe<string>

  details?: Maybe<string>

  unit?: Maybe<string>

  resultType?: Maybe<string>

  resultTarget?: Maybe<Json>

  recurrence?: Maybe<Json>

  resultTypeValue?: Maybe<string>

  resultBaseline?: Maybe<string>

  resultBaselineScale?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanKpiScaleOptionInput {
  label?: Maybe<string>

  value: string

  planKPIId: string

  active?: Maybe<boolean>

  position?: Maybe<number>
}

export interface IPlanObjectiveUpdateInput {
  treePosition?: Maybe<number>

  description?: Maybe<string>

  typeOptionId?: Maybe<string>

  ownedById?: Maybe<string>

  memberUserIds?: Maybe<Maybe<string>[]>

  memberPositionIds?: Maybe<Maybe<string>[]>

  color?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanObjectiveResourceInput {
  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  fieldValues?: Maybe<Maybe<IFieldValueInput>[]>
}

export interface IPlanTypeCreateInput {
  description?: Maybe<string>

  code?: Maybe<string>

  objectiveOwnerObjectSecurityId?: Maybe<string>

  objectiveMemberObjectSecurityId?: Maybe<string>

  actionOwnerObjectSecurityId?: Maybe<string>

  actionMemberObjectSecurityId?: Maybe<string>

  kpiOwnerObjectSecurityId?: Maybe<string>

  kpiMemberObjectSecurityId?: Maybe<string>

  planOwnerObjectSecurityId?: Maybe<string>

  planMemberObjectSecurityId?: Maybe<string>
}

export interface IPlanTypeUpdateInput {
  active?: Maybe<boolean>

  description?: Maybe<string>

  code?: Maybe<string>

  color?: Maybe<string>

  allowNesting?: Maybe<boolean>

  maximumNestedLevelsCount?: Maybe<number>

  levels?: Maybe<Maybe<IPlanTypeLevelInput>[]>

  objectiveOwnerObjectSecurityId?: Maybe<string>

  objectiveMemberObjectSecurityId?: Maybe<string>

  actionOwnerObjectSecurityId?: Maybe<string>

  actionMemberObjectSecurityId?: Maybe<string>

  kpiOwnerObjectSecurityId?: Maybe<string>

  kpiMemberObjectSecurityId?: Maybe<string>

  planOwnerObjectSecurityId?: Maybe<string>

  planMemberObjectSecurityId?: Maybe<string>
}

export interface IPlanTypeLevelInput {
  id?: Maybe<string>

  description: string

  color: string

  position?: Maybe<number>

  typeOptions?: Maybe<IPlanTypeLevelOptionInput[]>
}

export interface IPlanTypeLevelOptionInput {
  id: string

  label: string
}

export interface IPositionUpdateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>
}

export interface IPositionCreateInput {
  code?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  position?: Maybe<number>
}

export interface IPositionsTreeUpdateInput {
  id: string

  parentId?: Maybe<string>

  expanded: boolean

  position?: Maybe<number>
}

export interface IPowerBiReportCreateInput {
  workspaceId?: Maybe<string>
}

export interface IPowerBiReportUpdateInput {
  customName?: Maybe<string>

  availableTo?: {
    availableToUserOrPositionIds: Maybe<string[]>
  }
}

export interface IPowerBiReportCloneInput {
  fromWorkspaceId: string

  toWorkspaceId: string

  reportId: string
}

export interface IPowerBiReportCreateCallbackInput {
  workspaceId: string

  reportId: string
}

export interface IPowerBiTokenCreateInput {
  reportId?: Maybe<string>

  workspaceId?: Maybe<string>

  accessLevel?: Maybe<PowerBiTokenAcessLevels>
}

export interface IPowerBiStartExportToFileInput {
  format: ExportReportFileFormat

  reportId: string

  defaultBookmark?: Maybe<IPowerBiBookmarkInput>

  pages?: Maybe<Maybe<IPowerBiPageInput>[]>
}

export interface IPowerBiBookmarkInput {
  name?: Maybe<string>

  state?: Maybe<string>
}

export interface IPowerBiPageInput {
  bookmark?: Maybe<IPowerBiBookmarkInput>

  pageName?: Maybe<string>
}

export interface IPowerBiUpdateStandardReportInput {
  /** id of new report */
  reportId: string
}

export interface IReviewInput {
  planId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  startPeriod?: Maybe<DateString>

  endPeriodAt?: Maybe<DateString>

  dueAt?: Maybe<DateString>

  scheduledReviewId?: Maybe<string>

  status?: Maybe<ReviewStatus>

  values?: Maybe<IReviewValuesInput>
}

export interface IReviewValuesInput {
  goals?: Maybe<string>

  doWell?: Maybe<string>

  doBetter?: Maybe<string>
}

export interface IRiskCreateInput {
  parentRiskId?: Maybe<string>

  riskTemplateId: string

  recurrence?: Maybe<Json>
}

export interface IRiskUpdateInput {
  private?: Maybe<boolean>

  archived?: Maybe<boolean>

  recurrence?: Maybe<Json>

  isGuestAccess?: Maybe<boolean>

  customerUniqueIdPostfix?: Maybe<number>
}

export interface IRiskTemplateCreateInput {
  name?: Maybe<string>
}

export interface IRiskTemplateUpdateInput {
  name?: Maybe<string>

  private?: Maybe<boolean>

  archived?: Maybe<boolean>

  isGuestAccess?: Maybe<boolean>

  isPublicSearchable?: Maybe<boolean>

  generateCustomerUniqueId?: Maybe<boolean>

  customerUniqueIdPrefix?: Maybe<string>

  includeInAllAiContext?: Maybe<boolean>

  aiEnabled?: Maybe<boolean>

  aiContextDescription?: Maybe<string>

  archiveOnDelete?: Maybe<boolean>

  aiSuggestedPrompts?: Maybe<string[]>

  formConditions?: Maybe<IRiskTemplateFormConditionInput[]>
}

export interface IRiskTemplateFormConditionInput {
  id?: Maybe<string>

  fieldId?: Maybe<string>

  trigger?: Maybe<IRiskTemplateFormConditionTriggerInput>

  actions?: Maybe<IRiskTemplateFormConditionActionInput[]>
}

export interface IRiskTemplateFormConditionTriggerInput {
  value?: Maybe<Json>
}

export interface IRiskTemplateFormConditionActionInput {
  type?: Maybe<RiskTemplateFormConditionActionType>

  fieldId?: Maybe<string>

  value?: Maybe<Json>
}

export interface IRiskViewCreateInput {
  name: string

  riskTemplateId: string

  settings: IRiskViewSettingsInput
}

export interface IRiskViewSettingsInput {
  fields?: Maybe<Maybe<IRiskViewSettingsItemInput>[]>
}

export interface IRiskViewSettingsItemInput {
  fieldId?: Maybe<string>

  active?: Maybe<boolean>

  parentFieldId?: Maybe<string>

  fieldLinkId?: Maybe<string>

  riskTemplateId?: Maybe<string>
}

export interface IRiskViewUpdateInput {
  name?: Maybe<string>

  settings?: Maybe<IRiskViewSettingsInput>
}

export interface IScheduledReviewInput {
  planId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  repeating?: Maybe<boolean>

  repeatEveryValue?: Maybe<number>

  repeatEveryType?: Maybe<ScheduledReviewRepeatEveryType>

  periodEndsOn?: Maybe<ScheduledReviewPeriodEndsOn>

  startPeriod?: Maybe<DateString>

  endPeriod?: Maybe<DateString>

  endsOn?: Maybe<ScheduledReviewEndsOn>

  endsOnDate?: Maybe<DateString>

  scheduledReviewers: Maybe<IScheduledReviewReviewerInput>[]
}

export interface IScheduledReviewReviewerInput {
  id?: Maybe<string>

  userId?: Maybe<string>

  positionId?: Maybe<string>

  reviewDate?: Maybe<IScheduledReviewersDateInput>
}

export interface IScheduledReviewersDateInput {
  end?: Maybe<ScheduledReviewersDateEnd>

  value?: Maybe<number>

  date?: Maybe<DateString>

  period?: Maybe<ScheduledReviewersDatePeriod>
}

export interface ISecurityGroupInput {
  name?: Maybe<string>

  businessRules?: Maybe<Maybe<ISecurityGroupBusinessRuleInput>[]>

  active?: Maybe<boolean>
}

export interface ISecurityGroupBusinessRuleInput {
  users: IBusinesRuleUserInput[]

  views: IBusinesRuleViewInput[]
}

export interface IBusinesRuleUserInput {
  userOrPositionId?: Maybe<string>

  securityProfileId: string
}

export interface IBusinesRuleViewInput {
  canView?: Maybe<CanViewType>

  riskTemplateId?: Maybe<string>
}

export interface ISecurityProfileInput {
  name?: Maybe<string>

  permissions?: Maybe<ISecurityProfilePermissionsInput>

  active?: Maybe<boolean>
}

export interface ISecurityProfilePermissionsInput {
  /** General Functions */
  home?: Maybe<boolean>

  planRegister?: Maybe<boolean>

  createPlan?: Maybe<boolean>

  kpiRegister?: Maybe<boolean>

  createKPI?: Maybe<boolean>

  actionRegister?: Maybe<boolean>

  createAction?: Maybe<boolean>

  objectiveRegister?: Maybe<boolean>

  settings?: Maybe<boolean>

  globalSearch?: Maybe<boolean>
  /** Reporting Functions */
  reporting?: Maybe<boolean>

  reportingCreate?: Maybe<boolean>

  reportingEdit?: Maybe<boolean>

  reportingDelete?: Maybe<boolean>

  reportingClone?: Maybe<boolean>

  reportingRefresh?: Maybe<boolean>

  manageReports?: Maybe<boolean>
  /** Plan Functions */
  planModifyPlan?: Maybe<boolean>

  planDeletePlan?: Maybe<boolean>

  planCreateScheduledReview?: Maybe<boolean>

  planModifyScheduledReview?: Maybe<boolean>

  planDeleteScheduledReview?: Maybe<boolean>

  planViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  planViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  planLinkObjects?: Maybe<boolean>

  planAddItemsFromParentPlan?: Maybe<boolean>

  planAddObjective?: Maybe<boolean>

  planAddKPI?: Maybe<boolean>

  planAddAction?: Maybe<boolean>

  planSetPlanSecurity?: Maybe<boolean>

  planSetUserPlanSecurity?: Maybe<boolean>
  /** Objective Functions */
  objectiveModifyObjective?: Maybe<boolean>

  objectiveDeleteObjective?: Maybe<boolean>

  objectiveArchiveObjective?: Maybe<boolean>

  objectiveCreateScheduledReview?: Maybe<boolean>

  objectiveModifyScheduledReview?: Maybe<boolean>

  objectiveDeleteScheduledReview?: Maybe<boolean>

  objectiveViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  objectiveViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  objectiveLinkObjects?: Maybe<boolean>

  objectiveCreateResource?: Maybe<boolean>

  objectiveModifyResource?: Maybe<boolean>

  objectiveDeleteResource?: Maybe<boolean>

  objectiveAddObjective?: Maybe<boolean>

  objectiveAddAction?: Maybe<boolean>

  objectiveAddKPI?: Maybe<boolean>

  objectiveSetUserObjectiveSecurity?: Maybe<boolean>

  planCreateSubPlan?: Maybe<boolean>
  /** Action Functions */
  actionModifyAction?: Maybe<boolean>

  actionDeleteAction?: Maybe<boolean>

  actionArchiveAction?: Maybe<boolean>

  actionCreateScheduledReview?: Maybe<boolean>

  actionModifyScheduledReview?: Maybe<boolean>

  actionDeleteScheduledReview?: Maybe<boolean>

  actionViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  actionViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  actionLinkObjects?: Maybe<boolean>

  actionCreateResource?: Maybe<boolean>

  actionModifyResource?: Maybe<boolean>

  actionDeleteResource?: Maybe<boolean>

  actionAddObjective?: Maybe<boolean>

  actionAddAction?: Maybe<boolean>

  actionAddKPI?: Maybe<boolean>

  actionSetUserActionSecurity?: Maybe<boolean>

  /** AI Functions */

  aiViewUsage?: Maybe<boolean>

  aiManageSettings?: Maybe<boolean>

  aiEnableUsage?: Maybe<boolean>

  /** KPI Functions */
  kpiModifyKPI?: Maybe<boolean>

  kpiDeleteKPI?: Maybe<boolean>

  kpiArchiveKPI?: Maybe<boolean>

  kpiCreateScheduledReview?: Maybe<boolean>

  kpiModifyScheduledReview?: Maybe<boolean>

  kpiDeleteScheduledReview?: Maybe<boolean>

  kpiViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  kpiViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  kpiLinkObjects?: Maybe<boolean>

  kpiAddKPIResult?: Maybe<boolean>

  kpiModifyKPIResult?: Maybe<boolean>

  kpiDeleteKPIResult?: Maybe<boolean>

  kpiAddObjective?: Maybe<boolean>

  kpiAddAction?: Maybe<boolean>

  kpiAddKPI?: Maybe<boolean>

  kpiModifyUserKPISecurity?: Maybe<boolean>
  /** Administrations */
  adminViewPrivatePlans?: Maybe<boolean>

  adminLockPlans?: Maybe<boolean>
  /** riskTemplates */
  manageRiskTemplates?: Maybe<boolean>

  riskCreate?: Maybe<boolean>

  riskModify?: Maybe<boolean>

  riskDelete?: Maybe<boolean>

  riskSetSecurity?: Maybe<boolean>

  riskSetUserRiskSecurity?: Maybe<boolean>

  riskViewRelatedRisks?: Maybe<boolean>

  adminViewPrivateRisks?: Maybe<boolean>

  scheduleRisks?: Maybe<boolean>

  editReadOnlyFields?: Maybe<boolean>

  modifyOwnedGroupedRisks?: Maybe<boolean>

  manageDisabledRisks?: Maybe<boolean>

  manageDashboards?: Maybe<boolean>

  viewLinkedParentRisk?: Maybe<boolean>

  riskArchive?: Maybe<boolean>

  viewOrganizationLevelData?: Maybe<boolean>
}

export interface IPreviewEmailInput {
  subject?: Maybe<string>

  template?: Maybe<string>

  variables?: Maybe<Json>
}

export interface ISystemProcessInput {
  active?: Maybe<boolean>

  name?: Maybe<string>

  processType?: Maybe<SystemProcessType>

  object?: Maybe<SystemProcessObject>

  objectId?: Maybe<string>

  conditions?: Maybe<Maybe<ISystemProcessConditionInput>[]>

  transactions?: Maybe<Maybe<ISystemProcessTransactionInput>[]>
}

export interface IExecuteCustomFunctionTransactionInput {
  riskId: string

  processId: string
}

export interface ICheckAndNotifyInlineFunctionVisibilityInput {
  riskId: string

  riskTemplateId: string
}

export interface IExecuteInlineFunctionTransactionInput {
  riskId: string

  riskTemplateId: string

  inlineFunctionId: string

  fieldValueId?: Maybe<string>
}

export interface IUserCreateInput {
  firstName: string

  lastName: string

  companyId: string

  email: string

  businessUnitIds?: Maybe<Maybe<string>[]>

  authMethod: string

  username: string

  password: string

  externalEmployeeId?: Maybe<string>
}

export interface IUserUpdateInput {
  active?: Maybe<boolean>

  companyId?: Maybe<string>

  firstName?: Maybe<string>

  lastName?: Maybe<string>

  middleName?: Maybe<string>

  displayName?: Maybe<string>

  gender?: Maybe<UserGender>

  language?: Maybe<string>

  address?: Maybe<string>

  homePhone?: Maybe<string>

  mobilePhone?: Maybe<string>

  workPhone?: Maybe<string>

  timezone?: Maybe<string>

  avatarUrl?: Maybe<string>

  email?: Maybe<string>

  alternateEmail?: Maybe<string>

  reportsToId?: Maybe<string>

  employmentStatus?: Maybe<EmploymentStatus>

  code?: Maybe<string>

  authMethod?: Maybe<string>

  password?: Maybe<string>

  username?: Maybe<string>

  dateOfBirth?: Maybe<DateString>

  externalEmployeeId?: Maybe<string>

  positionIds?: Maybe<Maybe<string>[]>

  businessUnitIds?: Maybe<Maybe<string>[]>
}

export enum OrderDirection {
  Asc = 'ASC',
  Desc = 'DESC'
}

export enum UserRoles {
  SkeftoAdmin = 'SKEFTO_ADMIN',
  CompanyAdmin = 'COMPANY_ADMIN'
}

export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE'
}

export enum UserGender {
  Male = 'MALE',
  Female = 'FEMALE',
  Other = 'OTHER'
}

export enum EmploymentStatus {
  FullTime = 'FULL_TIME',
  PartTime = 'PART_TIME'
}

export enum CanViewType {
  Plan = 'PLAN',
  RiskTemplate = 'RISK_TEMPLATE',
  IndependentKpi = 'INDEPENDENT_KPI',
  IndependentAction = 'INDEPENDENT_ACTION'
}

export enum SecurityProfileReviewPermission {
  None = 'NONE',
  Own = 'OWN',
  All = 'ALL'
}

export enum DashboardItemType {
  Section = 'SECTION',
  Image = 'IMAGE',
  Function = 'FUNCTION',
  Card = 'CARD',
  StrategyActions = 'STRATEGY_ACTIONS',
  BarChart = 'BAR_CHART',
  LineChart = 'LINE_CHART',
  PieChart = 'PIE_CHART'
}

export enum DashboardItemFunctionType {
  Hyperlink = 'HYPERLINK',
  CreateRisk = 'CREATE_RISK'
}

export enum FilterTarget {
  Action = 'ACTION',
  Plan = 'PLAN',
  Kpi = 'KPI',
  Objective = 'OBJECTIVE',
  RiskTemplate = 'RISK_TEMPLATE'
}

export enum FieldType {
  Switch = 'SWITCH',
  Date = 'DATE',
  PickList = 'PICK_LIST',
  Text = 'TEXT',
  Number = 'NUMBER',
  Lookup = 'LOOKUP',
  Tag = 'TAG',
  Json = 'JSON',
  Address = 'ADDRESS',
  Table = 'TABLE',
  RiskMatrix = 'RISK_MATRIX',
  Label = 'LABEL',
  DocumentOrLink = 'DOCUMENT_OR_LINK',
  Section = 'SECTION',
  Form = 'FORM',
  LinkedForm = 'LINKED_FORM',
  RecordLinks = 'RECORD_LINKS',
  ParentRelatedForms = 'PARENT_RELATED_FORMS'
}

export enum FieldSource {
  Plans = 'PLANS',
  PlanTypes = 'PLAN_TYPES',
  Kpis = 'KPIS',
  Actions = 'ACTIONS',
  Objectives = 'OBJECTIVES',
  Users = 'USERS',
  Positions = 'POSITIONS',
  BusinessActivities = 'BUSINESS_ACTIVITIES',
  BusinessProcesses = 'BUSINESS_PROCESSES',
  BusinessUnits = 'BUSINESS_UNITS',
  Locations = 'LOCATIONS',
  Risks = 'RISKS'
}

export enum LogEntityTypes {
  Objective = 'OBJECTIVE',
  Resource = 'RESOURCE',
  Comment = 'COMMENT',
  Action = 'ACTION',
  Kpi = 'KPI',
  Plan = 'PLAN',
  Review = 'REVIEW',
  Image = 'IMAGE',
  Link = 'LINK',
  FieldValue = 'FIELD_VALUE'
}

export enum LogActionTypes {
  Create = 'CREATE',
  Update = 'UPDATE',
  Delete = 'DELETE'
}

export enum InputMethod {
  UserInput = 'USER_INPUT',
  Integration = 'INTEGRATION'
}

export enum ScheduledReviewRepeatEveryType {
  Monthly = 'MONTHLY',
  Weekly = 'WEEKLY'
}

export enum ScheduledReviewPeriodEndsOn {
  TheSameDay = 'THE_SAME_DAY',
  LastDay = 'LAST_DAY'
}

export enum ScheduledReviewersDateEnd {
  BeforePeriodEnd = 'BEFORE_PERIOD_END',
  AfterPeriodEnd = 'AFTER_PERIOD_END',
  AtPeriodEnd = 'AT_PERIOD_END'
}

export enum ScheduledReviewersDatePeriod {
  Days = 'DAYS',
  Weeks = 'WEEKS',
  Months = 'MONTHS'
}

export enum ScheduledReviewEndsOn {
  Never = 'NEVER',
  On = 'ON'
}

export enum ReviewStatus {
  NotStarted = 'NOT_STARTED',
  InProgress = 'IN_PROGRESS',
  Complete = 'COMPLETE'
}

export enum SystemProcessConditionType {
  And = 'AND',
  Or = 'OR'
}

export enum SystemProcessObject {
  Plan = 'PLAN',
  Action = 'ACTION',
  Objective = 'OBJECTIVE',
  Kpi = 'KPI',
  RiskTemplate = 'RISK_TEMPLATE'
}

export enum SystemProcessActionType {
  Completed = 'COMPLETED',
  Reopened = 'REOPENED'
}

export enum SystemProcessConditionAction {
  Is = 'IS',
  IsNot = 'IS_NOT',
  HasChanged = 'HAS_CHANGED',
  Contains = 'CONTAINS',
  LessThan = 'LESS_THAN',
  GreaterThan = 'GREATER_THAN',
  DaysUntil = 'DAYS_UNTIL',
  DaysSince = 'DAYS_SINCE',
  IsNull = 'IS_NULL',
  IsNotNull = 'IS_NOT_NULL',
  AlwaysTrue = 'ALWAYS_TRUE'
}

export enum SystemProcessTransactionType {
  SendEmail = 'SEND_EMAIL',
  SetField = 'SET_FIELD',
  ShowFunction = 'SHOW_FUNCTION',
  ShowInlineFunction = 'SHOW_INLINE_FUNCTION',
  ShowRelatedForm = 'SHOW_RELATED_FORM',
  CreateAction = 'CREATE_ACTION',
  CreateRiskRecurrence = 'CREATE_RISK_RECURRENCE',
  DisableRiskRecurrence = 'DISABLE_RISK_RECURRENCE',
  EnableRiskRecurrence = 'ENABLE_RISK_RECURRENCE',
  Confirmation = 'CONFIRMATION',
  ResponseMessage = 'RESPONSE_MESSAGE',
  CheckMandatory = 'CHECK_MANDATORY',
  DisableUpdate = 'DISABLE_UPDATE',
  DisableDelete = 'DISABLE_DELETE',
  Restrict = 'RESTRICT',
  EnableGroupAccess = 'ENABLE_GROUP_ACCESS',
  EnableSupervisorHierarchy = 'ENABLE_SUPERVISOR_HIERARCHY',
  RemoveGuestAccess = 'REMOVE_GUEST_ACCESS',
  MapField = 'MAP_FIELD',
  AccessFromRelatedForm = 'ACCESS_FROM_RELATED_FORM',
  CreateRelatedForm = 'CREATE_RELATED_FORM'
}

export enum SystemProcessTransactionSendTo {
  Assignee = 'ASSIGNEE',
  Members = 'MEMBERS',
  ReportsToPerson = 'REPORTS_TO_PERSON',
  ReportsToPosition = 'REPORTS_TO_POSITION',
  Reviewer = 'REVIEWER'
}

export enum PowerBiDatasetRefreshStatus {
  Unknown = 'Unknown',
  Completed = 'Completed',
  Failed = 'Failed',
  Disabled = 'Disabled'
}

export enum RiskTemplateFormConditionActionType {
  Visibility = 'VISIBILITY',
  LimitOptions = 'LIMIT_OPTIONS',
  LimitResponse = 'LIMIT_RESPONSE'
}

export enum SystemProcessType {
  Trigger = 'TRIGGER',
  Automation = 'AUTOMATION'
}

export enum AssetType {
  Avatar = 'AVATAR',
  ActivityFile = 'ACTIVITY_FILE',
  ActivityLink = 'ACTIVITY_LINK',
  CompanyLogo = 'COMPANY_LOGO'
}

export enum CompanySubscriptionLevel {
  Standard = 'STANDARD',
  Professional = 'PROFESSIONAL',
  Enterprise = 'ENTERPRISE'
}

export enum PowerBiTokenAcessLevels {
  Create = 'Create',
  View = 'View',
  Edit = 'Edit'
}

export enum ExportReportFileFormat {
  Pdf = 'PDF',
  Pptx = 'PPTX'
}

/** Date in ISO String */
export type DateString = any

/** The `JSON` scalar type represents JSON values as specified by [ECMA-404](http://www.ecma-international.org/publications/files/ECMA-ST/ECMA-404.pdf). */
export type Json = any

// ====================================================
// Scalars
// ====================================================

// ====================================================
// Interfaces
// ====================================================

export interface IIResponse {
  ok: boolean

  message?: Maybe<string>
}

// ====================================================
// Types
// ====================================================

export interface IQuery {
  _?: Maybe<boolean>

  apiTokens?: Maybe<IApiTokensResponse>

  businessActivities?: Maybe<IBusinessActivitiesResponse>

  businessProcesses?: Maybe<IBusinessProcessesResponse>

  businessUnits?: Maybe<IBusinessUnitsResponse>

  businessUnitsHierarchy?: Maybe<IBusinessUnitsHierarchyResponse>

  globalGraph?: Maybe<IGlobalGraphResponse>

  companies?: Maybe<ICompaniesResponse>

  getCompanyRiskResponseTypes?: Maybe<IGetCompanyRiskResponseTypesResponse>

  companyFields?: Maybe<ICompanyFieldsResponse>

  dashboards?: Maybe<IGetDashboardsResponse>

  dashboardItems?: Maybe<IGetDashboardItemsResponse>

  filters?: Maybe<IFiltersResponse>

  globalSearch?: Maybe<IGlobalSearchResponse>

  activeDirectories?: Maybe<IActiveDirectoriesResponse>

  activeDirectoryGetAdminAuthUrl: string

  linkingItems?: Maybe<ILinkingItemsResponse>

  locations?: Maybe<ILocationsResponse>

  logs?: Maybe<ILogsResponse>

  objectEntities?: Maybe<IObjectEntitiesResponse>

  objectSecurities?: Maybe<IObjectSecuritiesResponse>

  plans?: Maybe<IPlansResponse>

  homePlans?: Maybe<IHomePageResponse>

  planActions?: Maybe<IPlanActionsResponse>

  planItems?: Maybe<IPlanItemsResponse>

  planKPIs?: Maybe<IPlanKpIsResponse>

  planObjectives?: Maybe<IPlanObjectivesResponse>

  planResources?: Maybe<IPlanObjectiveResourcesResponse>

  planTypes?: Maybe<IPlanTypesResponse>

  positions?: Maybe<IPositionsResponse>

  powerBIWorkspaces?: Maybe<IPowerBiWorkspacesResponse>

  powerBIReports?: Maybe<IPowerBiReportsResponse>

  powerBIDatasetRefreshedAt?: Maybe<IPowerBiDatasetRefreshedAtResponse>

  powerBICheckExportToFileStatus: IPowerBiCheckExportToFileStatusResponse

  powerBIStandardReports?: Maybe<IPowerBiStandardReportsResponse>

  risks?: Maybe<IRisksResponse>

  risksPublic?: Maybe<IRisksResponse>

  getRelatedFormsPermissions?: Maybe<IGetRelatedFormsPermissionsResponse>

  getGroupedFormsPermissions?: Maybe<IGetGroupedFormsPermissionsResponse>

  getDataForExportRisk?: Maybe<IFieldValue[][]>

  riskTemplates?: Maybe<IRiskTemplatesResponse>

  riskTemplatesPublic?: Maybe<IRiskTemplatesResponse>

  riskViews?: Maybe<IRiskViewsResponse>

  securityGroups?: Maybe<ISecurityGroupsResponse>

  permissionsFromSecurityGroups?: Maybe<IPrmissionsFromSecurityGroupsResponse>

  securityProfiles?: Maybe<ISecurityProfilesResponse>

  systemProcesses?: Maybe<ISystemProcessesResponse>

  systemProcessesPublic?: Maybe<ISystemProcessesResponse>

  getInlineFunctions?: Maybe<IInlineFunction[]>

  userMe: IUserResponse

  users?: Maybe<IUsersResponse>

  myTeam?: Maybe<IUsersResponse>

  usersOrPositions?: Maybe<IUsersOrPositionsResponse>
}

export interface IApiTokensResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IApiToken>[]>
}

export interface IPagination {
  pageTake: number

  pageSkip: number

  totalCount?: Maybe<number>
}

export interface IApiToken {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  token?: Maybe<string>
}

export interface IUser {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  mobilePhone?: Maybe<string>

  language?: Maybe<string>

  email?: Maybe<string>

  lastName?: Maybe<string>

  timezone?: Maybe<string>

  companyId?: Maybe<string>

  homePhone?: Maybe<string>

  workPhone?: Maybe<string>

  firstName?: Maybe<string>

  displayName?: Maybe<string>

  code?: Maybe<string>

  reportsToId?: Maybe<string>

  avatarUrl?: Maybe<string>

  middleName?: Maybe<string>

  alternateEmail?: Maybe<string>

  dateOfBirth?: Maybe<DateString>

  reportsTo?: Maybe<IUser>

  positions?: Maybe<Maybe<IPosition>[]>

  businessUnits?: Maybe<Maybe<IBusinessUnit>[]>

  address?: Maybe<string>

  roles?: Maybe<Maybe<UserRoles>[]>

  status?: Maybe<UserStatus>

  gender?: Maybe<UserGender>

  employmentStatus?: Maybe<EmploymentStatus>

  authMethod?: Maybe<string>

  password?: Maybe<string>

  username?: Maybe<string>

  totpEnabled?: Maybe<boolean>

  active?: Maybe<boolean>

  company?: Maybe<ICompany>

  securityGroups?: Maybe<Maybe<ISecurityGroup>[]>

  controlledIds?: Maybe<Maybe<string>[]>

  userAndPositionIds?: Maybe<Maybe<string>[]>

  permissionsFromSecurityGroups?: Maybe<ISecurityProfilePermissions>

  externalEmployeeId?: Maybe<string>
  /** active directory */
  activeDirectoryId?: Maybe<string>

  activeDirectoryTenantId?: Maybe<string>

  activeDirectoryAdministrativeUnits?: Maybe<IActiveDirectoryAdministrativeUnit[]>

  locations?: Maybe<ILocation[]>

  refreshTokenAbsoluteTtl?: Maybe<number>

  idleTimeoutSeconds?: Maybe<number>
}

export interface IPosition {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  parentId?: Maybe<string>

  userId?: Maybe<string>

  active?: Maybe<boolean>

  expanded?: Maybe<boolean>

  position?: Maybe<number>

  description?: Maybe<string>

  code?: Maybe<string>

  parent?: Maybe<IPosition>

  user?: Maybe<IUser>
}

export interface IBusinessUnit {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  expanded?: Maybe<boolean>

  position?: Maybe<number>

  description?: Maybe<string>

  code?: Maybe<string>

  parent?: Maybe<IBusinessUnit>

  users?: Maybe<IUser[]>
}

export interface ICompanyDataPrivacySettings {
  publicBusinessUnits: boolean
  publicLocations: boolean
  publicPositions: boolean
  publicResponseTypeOptions: boolean
  publicUsers: boolean
}

export interface ICompany {
  id?: Maybe<string>

  name?: Maybe<string>

  code?: Maybe<string>

  address?: Maybe<string>

  domainName?: Maybe<string>

  language?: Maybe<string>

  dateAndNumberFormat?: Maybe<string>

  financialYearStartsAt?: Maybe<DateString>

  financialYearFinishesAt?: Maybe<DateString>

  sector?: Maybe<Maybe<string>[]>

  timezone?: Maybe<string>

  businessDays?: Maybe<Json>

  legitimateInterest?: Maybe<string>

  privacyPolicy?: Maybe<string>

  consentToCommunicate?: Maybe<string>

  licences?: Maybe<Json>

  currency?: Maybe<string>

  businessNumber?: Maybe<string>

  companyNumber?: Maybe<string>

  logoUrl?: Maybe<string>

  logoUrlFilename?: Maybe<string>

  dataPrivacySettings?: Maybe<ICompanyDataPrivacySettings>

  refreshTokenAbsoluteTtl?: Maybe<number>

  idleTimeoutSeconds?: Maybe<number>

  twoFactorEnforced?: Maybe<boolean>
}

export interface ISecurityGroup {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  name?: Maybe<string>

  businessRules?: Maybe<Maybe<ISecurityGroupBusinessRule>[]>

  active?: Maybe<boolean>
}

export interface ISecurityGroupBusinessRule {
  users: IBusinesRuleUser[]

  views: IBusinesRuleView[]
}

export interface IBusinesRuleUser {
  userOrPositionId?: Maybe<string>

  securityProfileId: string
}

export interface IBusinesRuleView {
  canView?: Maybe<CanViewType>

  riskTemplateId?: Maybe<string>
}

export interface ISecurityProfilePermissions {
  /** General Functions */
  home?: Maybe<boolean>

  planRegister?: Maybe<boolean>

  createPlan?: Maybe<boolean>

  kpiRegister?: Maybe<boolean>

  createKPI?: Maybe<boolean>

  actionRegister?: Maybe<boolean>

  createAction?: Maybe<boolean>

  objectiveRegister?: Maybe<boolean>

  settings?: Maybe<boolean>

  risksRegister?: Maybe<boolean>

  globalSearch?: Maybe<boolean>
  /** reporting functions */
  reporting?: Maybe<boolean>

  reportingCreate?: Maybe<boolean>

  reportingEdit?: Maybe<boolean>

  reportingDelete?: Maybe<boolean>

  reportingClone?: Maybe<boolean>

  reportingRefresh?: Maybe<boolean>

  manageReports?: Maybe<boolean>
  /** Plan Functions */
  planModifyPlan?: Maybe<boolean>

  planDeletePlan?: Maybe<boolean>

  planCreateScheduledReview?: Maybe<boolean>

  planModifyScheduledReview?: Maybe<boolean>

  planDeleteScheduledReview?: Maybe<boolean>

  planViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  planViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  planLinkObjects?: Maybe<boolean>

  planAddItemsFromParentPlan?: Maybe<boolean>

  planAddObjective?: Maybe<boolean>

  planAddKPI?: Maybe<boolean>

  planAddAction?: Maybe<boolean>

  planSetPlanSecurity?: Maybe<boolean>

  planSetUserPlanSecurity?: Maybe<boolean>

  planCreateSubPlan?: Maybe<boolean>
  /** Objective Functions */
  objectiveModifyObjective?: Maybe<boolean>

  objectiveDeleteObjective?: Maybe<boolean>

  objectiveArchiveObjective?: Maybe<boolean>

  objectiveCreateScheduledReview?: Maybe<boolean>

  objectiveModifyScheduledReview?: Maybe<boolean>

  objectiveDeleteScheduledReview?: Maybe<boolean>

  objectiveViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  objectiveViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  objectiveLinkObjects?: Maybe<boolean>

  objectiveCreateResource?: Maybe<boolean>

  objectiveModifyResource?: Maybe<boolean>

  objectiveDeleteResource?: Maybe<boolean>

  objectiveAddObjective?: Maybe<boolean>

  objectiveAddAction?: Maybe<boolean>

  objectiveAddKPI?: Maybe<boolean>

  objectiveSetUserObjectiveSecurity?: Maybe<boolean>
  /** Action Functions */
  actionModifyAction?: Maybe<boolean>

  actionDeleteAction?: Maybe<boolean>

  actionArchiveAction?: Maybe<boolean>

  actionCreateScheduledReview?: Maybe<boolean>

  actionModifyScheduledReview?: Maybe<boolean>

  actionDeleteScheduledReview?: Maybe<boolean>

  actionViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  actionViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  actionLinkObjects?: Maybe<boolean>

  actionCreateResource?: Maybe<boolean>

  actionModifyResource?: Maybe<boolean>

  actionDeleteResource?: Maybe<boolean>

  actionAddObjective?: Maybe<boolean>

  actionAddAction?: Maybe<boolean>

  actionAddKPI?: Maybe<boolean>

  actionSetUserActionSecurity?: Maybe<boolean>

  /** AI Functions */

  aiViewUsage?: Maybe<boolean>

  aiManageSettings?: Maybe<boolean>

  aiEnableUsage?: Maybe<boolean>

  /** KPI Functions */
  kpiModifyKPI?: Maybe<boolean>

  kpiDeleteKPI?: Maybe<boolean>

  kpiArchiveKPI?: Maybe<boolean>

  kpiCreateScheduledReview?: Maybe<boolean>

  kpiModifyScheduledReview?: Maybe<boolean>

  kpiDeleteScheduledReview?: Maybe<boolean>

  kpiViewInProgressReview?: Maybe<SecurityProfileReviewPermission>

  kpiViewCompletedReview?: Maybe<SecurityProfileReviewPermission>

  kpiLinkObjects?: Maybe<boolean>

  kpiAddKPIResult?: Maybe<boolean>

  kpiModifyKPIResult?: Maybe<boolean>

  kpiDeleteKPIResult?: Maybe<boolean>

  kpiAddObjective?: Maybe<boolean>

  kpiAddAction?: Maybe<boolean>

  kpiAddKPI?: Maybe<boolean>

  kpiModifyUserKPISecurity?: Maybe<boolean>
  /** administrations */
  adminViewPrivatePlans?: Maybe<boolean>

  adminLockPlans?: Maybe<boolean>
  /** riskTemplates */
  manageRiskTemplates?: Maybe<boolean>

  riskCreate?: Maybe<boolean>

  riskModify?: Maybe<boolean>

  riskDelete?: Maybe<boolean>

  riskSetSecurity?: Maybe<boolean>

  riskSetUserRiskSecurity?: Maybe<boolean>

  riskViewRelatedRisks?: Maybe<boolean>

  adminViewPrivateRisks?: Maybe<boolean>

  manageDisabledRisks?: Maybe<boolean>

  scheduleRisks?: Maybe<boolean>

  modifyOwnedGroupedRisks?: Maybe<boolean>

  editReadOnlyFields?: Maybe<boolean>

  manageDashboards?: Maybe<boolean>

  viewLinkedParentRisk?: Maybe<boolean>

  riskArchive?: Maybe<boolean>

  viewOrganizationLevelData?: Maybe<boolean>
}

export interface IActiveDirectoryAdministrativeUnit {
  id?: Maybe<string>

  displayName?: Maybe<string>
}

export interface IBusinessActivitiesResponse {
  pagination: IPagination

  data: Maybe<IBusinessActivity>[]
}

export interface IBusinessActivity {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  expanded?: Maybe<boolean>

  position?: Maybe<number>

  description?: Maybe<string>

  code?: Maybe<string>

  parent?: Maybe<IBusinessActivity>
}

export interface IBusinessProcessesResponse {
  pagination: IPagination

  data: Maybe<IBusinessProcess>[]
}

export interface IBusinessProcess {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  expanded?: Maybe<boolean>

  position?: Maybe<number>

  description?: Maybe<string>

  code?: Maybe<string>

  parent?: Maybe<IBusinessProcess>
}

export interface IBusinessUnitsResponse {
  pagination: IPagination

  data: Maybe<IBusinessUnit>[]
}

export interface IBusinessUnitsHierarchyResponse {
  data?: Maybe<Maybe<IBusinessUnitsHierarchyItem>[]>
}

export interface IBusinessUnitsHierarchyItem {
  id?: Maybe<string>

  description?: Maybe<string>

  parentId?: Maybe<string>

  type?: Maybe<string>

  planId?: Maybe<string>

  planCompanyId?: Maybe<string>

  planParentPlanId?: Maybe<string>
}

export interface IGlobalGraphResponse {
  data?: Maybe<IGlobalGraphData>
}

export interface IGlobalGraphData {
  nodes?: Maybe<Maybe<IGlobalGraphDataNode>[]>

  links?: Maybe<Maybe<IGlobalGraphDataLink>[]>
}

export interface IGlobalGraphDataNode {
  id?: Maybe<string>

  type?: Maybe<string>

  description?: Maybe<string>

  planCompanyId?: Maybe<string>

  planParentPlanId?: Maybe<string>
}

export interface IGlobalGraphDataLink {
  source?: Maybe<string>

  target?: Maybe<string>
}

export interface ICompaniesResponse {
  pagination: IPagination

  data: Maybe<ICompany>[]
}

export interface IGetCompanyRiskResponseTypesResponse {
  responseTypesSets: IResponseTypesSet[]
}

export interface IResponseTypesSet {
  id: string

  options: IRiskResponseTypeOption[]
}

export interface IRiskResponseTypeOption {
  id: string

  label?: Maybe<string>

  value?: Maybe<string>

  score?: Maybe<string>

  active?: Maybe<boolean>

  color?: Maybe<string>
}

export interface ICompanyFieldsResponse {
  pagination: IPagination

  data: Maybe<ICompanyField>[]
}

export interface ICompanyField {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  name?: Maybe<string>

  values?: Maybe<Json>

  company?: Maybe<ICompany>
}

export interface IGetDashboardsResponse {
  data: IDashboard[]
}

export interface IDashboard {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  active?: Maybe<boolean>

  title?: Maybe<string>

  description?: Maybe<string>

  availableTo?: Maybe<IDashboardAvailableTo>
}

export interface IDashboardAvailableTo {
  availableToUserIds?: Maybe<string[]>
}

export interface IGetDashboardItemsResponse {
  data: IGetDashboardItemsResponseItem[]
}

export interface IGetDashboardItemsResponseItem {
  item: IDashboardItem

  data: IDashboardItemData
}

export interface IDashboardItem {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  dashboardId?: Maybe<string>

  parentDashboardItemId?: Maybe<string>

  position?: Maybe<number>

  itemType?: Maybe<DashboardItemType>

  settings?: Maybe<IDashboardItemSettings>
}

export interface IDashboardItemSettings {
  title?: Maybe<string>

  description?: Maybe<string>

  width?: Maybe<number>

  height?: Maybe<number>

  padding?: Maybe<number>

  transparency?: Maybe<number>

  showTitle?: Maybe<boolean>

  dropShadow?: Maybe<boolean>

  openInWindow?: Maybe<boolean>

  imageUrl?: Maybe<string>

  imageFilename?: Maybe<string>

  riskTemplateId?: Maybe<string>

  riskViewId?: Maybe<string>

  filterId?: Maybe<string>

  functionType?: Maybe<DashboardItemFunctionType>

  functionLink?: Maybe<string>
}

export interface IDashboardItemData {
  risksCount?: Maybe<number>
}

export interface IFiltersResponse {
  pagination: IPagination

  data: Maybe<IFilter>[]
}

export interface IFilter {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  ownedById?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  name?: Maybe<string>

  args?: Maybe<Json>

  meta?: Maybe<Json>

  slug?: Maybe<string>

  description?: Maybe<string>

  target?: Maybe<FilterTarget>

  targetId?: Maybe<string>

  company?: Maybe<ICompany>

  ownedBy?: Maybe<IUser>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>
}

export interface IGlobalSearchResponse {
  pagination?: Maybe<IPagination>

  plans?: Maybe<Maybe<IPlan>[]>

  planKPIs?: Maybe<Maybe<IPlanKpi>[]>

  planActions?: Maybe<Maybe<IPlanAction>[]>

  planObjectives?: Maybe<Maybe<IPlanObjective>[]>
}

export interface IPlan {
  id?: Maybe<string>

  columnSettings?: Maybe<Json>

  scheduleDefaults?: Maybe<IPlanScheduleDefault[]>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  private?: Maybe<boolean>

  locked?: Maybe<boolean>

  company?: Maybe<ICompany>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  ownedById?: Maybe<string>

  planTypeId?: Maybe<string>

  name?: Maybe<string>

  status?: Maybe<string>

  planType?: Maybe<IPlanType>

  ownedByUser?: Maybe<IUser>

  ownedByPosition?: Maybe<IPosition>

  businessUnits?: Maybe<Maybe<IBusinessUnit>[]>

  businessProcesses?: Maybe<Maybe<IBusinessProcess>[]>

  businessActivities?: Maybe<Maybe<IBusinessActivity>[]>

  memberUsers?: Maybe<Maybe<IUser>[]>

  memberPositions?: Maybe<Maybe<IPosition>[]>

  logs?: Maybe<Maybe<ILog>[]>
  /** referencing & linking */
  parentPlanId?: Maybe<string>

  parentPlan?: Maybe<IPlan>

  subPlans?: Maybe<Maybe<IPlan>[]>

  linkedPlans?: Maybe<Maybe<IPlan>[]>

  scheduledReviews?: Maybe<Maybe<IScheduledReview>[]>

  reviews?: Maybe<Maybe<IReview>[]>

  planItems?: Maybe<IPlanItemsResponse>

  fields?: Maybe<Maybe<IField>[]>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  currentPermissions?: Maybe<ISecurityProfilePermissions>

  isBookmark?: Maybe<boolean>
}

export interface IPlanType {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  active?: Maybe<boolean>

  description?: Maybe<string>

  code?: Maybe<string>

  color?: Maybe<string>

  allowNesting?: Maybe<boolean>

  maximumNestedLevelsCount?: Maybe<number>

  levels?: Maybe<Maybe<IPlanTypeLevel>[]>

  objectiveOwnerObjectSecurityId?: Maybe<string>

  objectiveMemberObjectSecurityId?: Maybe<string>

  actionOwnerObjectSecurityId?: Maybe<string>

  actionMemberObjectSecurityId?: Maybe<string>

  kpiOwnerObjectSecurityId?: Maybe<string>

  kpiMemberObjectSecurityId?: Maybe<string>

  planOwnerObjectSecurityId?: Maybe<string>

  planMemberObjectSecurityId?: Maybe<string>

  objectiveOwnerObjectSecurity?: Maybe<IObjectSecurity>

  objectiveMemberObjectSecurity?: Maybe<IObjectSecurity>

  actionOwnerObjectSecurity?: Maybe<IObjectSecurity>

  actionMemberObjectSecurity?: Maybe<IObjectSecurity>

  kpiOwnerObjectSecurity?: Maybe<IObjectSecurity>

  kpiMemberObjectSecurity?: Maybe<IObjectSecurity>

  planOwnerObjectSecurity?: Maybe<IObjectSecurity>

  planMemberObjectSecurity?: Maybe<IObjectSecurity>

  company?: Maybe<ICompany>

  fields?: Maybe<Maybe<IField>[]>
}

export interface IPlanTypeLevel {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  company?: Maybe<ICompany>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  description?: Maybe<string>

  color?: Maybe<string>

  planTypeId?: Maybe<string>

  position?: Maybe<number>

  typeOptions?: Maybe<Maybe<IPlanTypeLevelOption>[]>
}

export interface IPlanTypeLevelOption {
  id?: Maybe<string>

  label?: Maybe<string>
}

export interface IObjectSecurity {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  name?: Maybe<string>

  description?: Maybe<string>

  active?: Maybe<boolean>

  securityProfiles?: Maybe<Maybe<ISecurityProfile>[]>

  objectEntityId?: Maybe<string>

  objectEntity?: Maybe<IObjectEntity>
}

export interface ISecurityProfile {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  name?: Maybe<string>

  permissions?: Maybe<ISecurityProfilePermissions>

  active?: Maybe<boolean>
}

export interface IObjectEntity {
  id?: Maybe<string>

  name?: Maybe<string>

  apiName?: Maybe<string>

  memberObjectSecurityId?: Maybe<string>

  ownerObjectSecurityId?: Maybe<string>

  memberObjectSecurity?: Maybe<IObjectSecurity>

  ownerObjectSecurity?: Maybe<IObjectSecurity>

  fields?: Maybe<Maybe<IField>[]>

  reviewQuestions?: Maybe<IObjectEntityReviewQuestion[]>

  reviewFieldIds?: Maybe<string[]>
}

export interface IField {
  id?: Maybe<string>

  objectEntity?: Maybe<IObjectEntity>

  standard?: Maybe<boolean>

  restricted?: Maybe<boolean>

  hidden?: Maybe<boolean>

  fieldType?: Maybe<FieldType>

  name?: Maybe<string>

  label?: Maybe<string>

  active?: Maybe<boolean>

  wysiwyg?: Maybe<boolean>

  options?: Maybe<Maybe<IFieldOption>[]>

  format?: Maybe<Maybe<IFieldOption>[]>

  source?: Maybe<Maybe<FieldSource>[]>

  searchable?: Maybe<boolean>

  searchTemplateIds?: Maybe<string[]>

  multiline?: Maybe<boolean>

  multiple?: Maybe<boolean>

  required?: Maybe<boolean>

  readonly?: Maybe<boolean>

  displayEmail?: Maybe<boolean>

  hideName?: Maybe<boolean>

  includeHistoryInApiResponse?: Maybe<boolean>

  exportHeaders?: Maybe<boolean>

  responseType?: Maybe<boolean>

  responseTypeId?: Maybe<string>

  weight?: Maybe<number>

  settings?: Maybe<Json>

  objectEntityId?: Maybe<string>

  entityId?: Maybe<string>

  position?: Maybe<number>

  parentFieldId?: Maybe<string>

  parentField?: Maybe<IField>

  objectSecurityId?: Maybe<string>

  objectSecurity?: Maybe<IObjectSecurity>

  historyEnabled?: Maybe<boolean>

  backgroundColor?: Maybe<string>

  limitResponse?: Maybe<Json>

  hideOnPublic?: Maybe<boolean>

  isPublicOnly?: Maybe<boolean>

  showUserDetails?: Maybe<boolean>

  visibleUserDetailsFields?: Maybe<string[]>

  includeInAiContext?: Maybe<boolean>

  aiAutoPopulate?: Maybe<boolean>

  aiIncludeHistory?: Maybe<boolean>

  aiContextDescription?: Maybe<string>

  aiSuggestedPrompts?: Maybe<string[]>
}

export interface IFieldOption {
  id?: Maybe<string>

  label?: Maybe<string>

  value?: Maybe<string>

  active?: Maybe<boolean>
}

export interface IObjectEntityReviewQuestion {
  id?: Maybe<string>

  position?: Maybe<number>

  text?: Maybe<string>
}

export interface ILog {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  entityId?: Maybe<string>

  entityType?: Maybe<LogEntityTypes>

  actionType?: Maybe<LogActionTypes>

  user?: Maybe<IUser>

  objective?: Maybe<IPlanObjective>

  action?: Maybe<IPlanAction>

  kpi?: Maybe<IPlanKpi>

  plan?: Maybe<IPlan>

  metadata?: Maybe<ILogMetadata>

  logs?: Maybe<Maybe<ILog>[]>
}

export interface IPlanObjective {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  company?: Maybe<ICompany>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  ownedById?: Maybe<string>

  description?: Maybe<string>

  planTypeId?: Maybe<string>

  typeOptionId?: Maybe<string>

  treePosition?: Maybe<number>

  plan?: Maybe<IPlan>

  ownedByUser?: Maybe<IUser>

  ownedByPosition?: Maybe<IPosition>

  memberUsers?: Maybe<Maybe<IUser>[]>

  memberPositions?: Maybe<Maybe<IPosition>[]>

  logs?: Maybe<Maybe<ILog>[]>

  resources?: Maybe<Maybe<IPlanObjectiveResource>[]>

  planItems?: Maybe<IPlanItemsResponse>

  destinationLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceParent?: Maybe<IPlanItem>

  scheduledReviews?: Maybe<Maybe<IScheduledReview>[]>

  reviews?: Maybe<Maybe<IReview>[]>

  fields?: Maybe<Maybe<IField>[]>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  color?: Maybe<string>
  /** READ ONLY */
  commencementAt?: Maybe<DateString>

  completionAt?: Maybe<DateString>

  completionPercentage?: Maybe<number>

  archived?: Maybe<boolean>

  currentPermissions?: Maybe<ISecurityProfilePermissions>

  isBookmark?: Maybe<boolean>
}

export interface IPlanObjectiveResource {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  createdById?: Maybe<string>

  createdBy?: Maybe<IUser>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  planObjective?: Maybe<IPlanObjective>

  planAction?: Maybe<IPlanAction>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>
}

export interface IPlanAction {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  company?: Maybe<ICompany>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  planId?: Maybe<string>

  ownedById?: Maybe<string>

  description?: Maybe<string>

  planTypeId?: Maybe<string>

  treePosition?: Maybe<number>

  milestone?: Maybe<boolean>

  commencementAt?: Maybe<DateString>

  completionAt?: Maybe<DateString>

  completionPercentage?: Maybe<number>

  ownedByUser?: Maybe<IUser>

  ownedByPosition?: Maybe<IPosition>

  memberUsers?: Maybe<Maybe<IUser>[]>

  memberPositions?: Maybe<Maybe<IPosition>[]>

  businessUnits?: Maybe<Maybe<IBusinessUnit>[]>

  businessProcesses?: Maybe<Maybe<IBusinessProcess>[]>

  businessActivities?: Maybe<Maybe<IBusinessActivity>[]>

  logs?: Maybe<Maybe<ILog>[]>

  resources?: Maybe<Maybe<IPlanObjectiveResource>[]>

  planItems?: Maybe<IPlanItemsResponse>

  destinationLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceParent?: Maybe<IPlanItem>

  scheduledReviews?: Maybe<Maybe<IScheduledReview>[]>

  reviews?: Maybe<Maybe<IReview>[]>

  fields?: Maybe<Maybe<IField>[]>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  archived?: Maybe<boolean>

  currentPermissions?: Maybe<ISecurityProfilePermissions>

  isBookmark?: Maybe<boolean>
}

export interface IPlanItemsResponse {
  pagination: IPagination

  data: Maybe<IPlanItem>[]
}

export interface IPlanItem {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  planId?: Maybe<string>

  parentId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planTypeLevel?: Maybe<IPlanTypeLevel>

  planAction?: Maybe<IPlanAction>

  planKPI?: Maybe<IPlanKpi>

  planObjective?: Maybe<IPlanObjective>

  treePosition?: Maybe<number>

  referenced?: Maybe<boolean>

  expanded?: Maybe<boolean>

  archived?: Maybe<boolean>

  plan?: Maybe<IPlan>

  path?: Maybe<Maybe<string>[]>
}

export interface IPlanKpi {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  company?: Maybe<ICompany>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  ownedById?: Maybe<string>

  description?: Maybe<string>

  inputMethod?: Maybe<InputMethod>

  ownedByUser?: Maybe<IUser>

  ownedByPosition?: Maybe<IPosition>

  memberUsers?: Maybe<Maybe<IUser>[]>

  memberPositions?: Maybe<Maybe<IPosition>[]>

  businessUnits?: Maybe<Maybe<IBusinessUnit>[]>

  businessProcesses?: Maybe<Maybe<IBusinessProcess>[]>

  businessActivities?: Maybe<Maybe<IBusinessActivity>[]>

  logs?: Maybe<Maybe<ILog>[]>

  details?: Maybe<string>

  unit?: Maybe<string>

  resultType?: Maybe<string>

  resultTarget?: Maybe<Json>

  recurrence?: Maybe<Json>

  resultTypeValue?: Maybe<string>

  resultBaseline?: Maybe<string>

  resultBaselineScale?: Maybe<string>

  planItems?: Maybe<IPlanItemsResponse>

  destinationLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceLinkingItems?: Maybe<Maybe<ILinkingItem>[]>

  sourceParent?: Maybe<IPlanItem>

  scheduledReviews?: Maybe<Maybe<IScheduledReview>[]>

  reviews?: Maybe<Maybe<IReview>[]>

  fields?: Maybe<Maybe<IField>[]>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  archived?: Maybe<boolean>

  currentPermissions?: Maybe<ISecurityProfilePermissions>

  resultTypeScale?: Maybe<Maybe<IPlanKpiScaleOption>[]>

  isBookmark?: Maybe<boolean>
}

export interface ILinkingItem {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  sourcePlanKPIId?: Maybe<string>

  destinationPlanKPIId?: Maybe<string>

  sourcePlanActionId?: Maybe<string>

  destinationPlanActionId?: Maybe<string>

  sourcePlanObjectiveId?: Maybe<string>

  destinationPlanObjectiveId?: Maybe<string>

  sourcePlanKPI?: Maybe<IPlanKpi>

  destinationPlanKPI?: Maybe<IPlanKpi>

  sourcePlanAction?: Maybe<IPlanAction>

  destinationPlanAction?: Maybe<IPlanAction>

  sourcePlanObjective?: Maybe<IPlanObjective>

  destinationPlanObjective?: Maybe<IPlanObjective>

  description?: Maybe<string>

  parentId?: Maybe<string>

  weighting?: Maybe<number>
}

export interface IScheduledReview {
  id?: Maybe<string>

  planId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  companyId?: Maybe<string>

  repeating?: Maybe<boolean>

  repeatEveryValue?: Maybe<number>

  repeatEveryType?: Maybe<ScheduledReviewRepeatEveryType>

  periodEndsOn?: Maybe<ScheduledReviewPeriodEndsOn>

  startPeriod?: Maybe<DateString>

  endPeriod?: Maybe<DateString>

  scheduledReviewers?: Maybe<Maybe<IScheduledReviewer>[]>

  endsOn?: Maybe<ScheduledReviewEndsOn>

  endsOnDate?: Maybe<DateString>
}

export interface IScheduledReviewer {
  id?: Maybe<string>

  userId?: Maybe<string>

  positionId?: Maybe<string>

  scheduledReviewId?: Maybe<string>

  reviewDate?: Maybe<IScheduledReviewersDate>

  user?: Maybe<IUser>

  position?: Maybe<IPosition>
}

export interface IScheduledReviewersDate {
  end?: Maybe<ScheduledReviewersDateEnd>

  value?: Maybe<number>

  date?: Maybe<DateString>

  period?: Maybe<ScheduledReviewersDatePeriod>
}

export interface IPlanScheduleDefault {
  entityType?: Maybe<string>

  levelId?: Maybe<string>

  repeating?: Maybe<boolean>

  repeatEveryValue?: Maybe<number>

  repeatEveryType?: Maybe<ScheduledReviewRepeatEveryType>

  periodEndsOn?: Maybe<ScheduledReviewPeriodEndsOn>

  startPeriod?: Maybe<DateString>

  endPeriod?: Maybe<DateString>

  endsOn?: Maybe<ScheduledReviewEndsOn>

  endsOnDate?: Maybe<DateString>

  reviewDate?: Maybe<IScheduledReviewersDate>
}

export interface IReview {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  planId?: Maybe<string>

  planObjectiveId?: Maybe<string>

  planActionId?: Maybe<string>

  planKPIId?: Maybe<string>

  startPeriod?: Maybe<DateString>

  endPeriodAt?: Maybe<DateString>

  dueAt?: Maybe<DateString>

  scheduledReviewId?: Maybe<string>

  scheduledReview?: Maybe<IScheduledReview>

  status?: Maybe<ReviewStatus>

  values?: Maybe<IReviewValues>

  createdById?: Maybe<string>

  updatedById?: Maybe<string>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>
}

export interface IReviewValues {
  goals?: Maybe<string>

  doWell?: Maybe<string>

  doBetter?: Maybe<string>
}

export interface IFieldValueMapItem {
  fieldValueId?: Maybe<string>

  parentFieldValueId?: Maybe<string>

  createdAt?: Maybe<DateString>

  fieldId?: Maybe<string>

  fieldLinkId?: Maybe<string>

  fieldName?: Maybe<string>

  fieldValue?: Maybe<Json>

  fieldType?: Maybe<FieldType>

  fieldLabel?: Maybe<string>

  wysiwyg?: Maybe<boolean>

  updatedAt?: Maybe<DateString>

  entityId?: Maybe<string>

  linkedFieldValue?: Maybe<IFieldValueMapItem>

  archived?: Maybe<boolean>
}

export interface IParentFormPropertiesItem {
  mainRisk: IRisk
  parentRisk?: IRisk
  ancestors: IRisk[]
  fieldValues: IFieldValueMapItem[]
}

export interface IPlanKpiScaleOption {
  id?: Maybe<string>

  label?: Maybe<string>

  value?: Maybe<string>

  planKPIId?: Maybe<string>

  active?: Maybe<boolean>

  position?: Maybe<number>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>
}

export interface ILogMetadata {
  resource?: Maybe<Json>

  fileName?: Maybe<string>

  fileUrl?: Maybe<string>

  fileContentType?: Maybe<string>

  comment?: Maybe<string>

  value?: Maybe<Json>
  /** Risk ID or Template ID */
  entityId?: Maybe<string>
}

export interface IActiveDirectoriesResponse {
  data: IActiveDirectory[]

  pagination: IPagination
}

export interface IActiveDirectory {
  id: string

  displayName?: Maybe<string>

  token: IToken
}

export interface IToken {
  id: string

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  mappings?: Maybe<ITokenMapping[]>
}

export interface ITokenMapping {
  id: string

  conditions: ISystemProcessCondition[]

  transactions: ISystemProcessTransaction[]
}

export interface ISystemProcessCondition {
  conditionType?: Maybe<SystemProcessConditionType>

  fieldLinkId?: Maybe<string>

  fieldName?: Maybe<string>

  entityName?: Maybe<SystemProcessObject>

  action?: Maybe<SystemProcessConditionAction>

  fieldType?: Maybe<FieldType>

  value?: Maybe<Json>

  inlineFunctionId?: Maybe<string>

  conditions?: Maybe<Maybe<ISystemProcessCondition>[]>
}

export interface IRelatedFormAccessList {
  transactionId: Maybe<string>
  riskTemplateId: Maybe<string>
  securityProfileId: Maybe<string>
}

export interface ICreateRelatedForm {
  riskTemplateId: Maybe<string>
  fieldValues?: Maybe<IFieldValueMapItem[]>
}

export interface ISystemProcessTransaction {
  id?: Maybe<string>

  transactionType?: Maybe<SystemProcessTransactionType>

  availableToFieldIds?: Maybe<string[]>

  availableToUserIds?: Maybe<string[]>

  availableToPositionIds?: Maybe<string[]>

  fieldName?: Maybe<string>

  fieldValue?: Maybe<Json>

  subject?: Maybe<string>

  template?: Maybe<string>

  recipients?: Maybe<Maybe<ISystemProcessTransactionRecipient>[]>

  functionName?: Maybe<string>

  fieldLinkId?: Maybe<string>

  inlineFunctionId?: Maybe<string>

  relatedForms?: Maybe<ISystemProcessRelatedForm>

  riskRecurrence?: Maybe<ISystemProcessTransactionRiskRecurrence>

  disableRiskRecurrence?: Maybe<ISystemProcessTransactionDisableRiskRecurrence>

  enableRiskRecurrence?: Maybe<ISystemProcessTransactionEnableRiskRecurrence>

  supervisorHierarchy?: Maybe<ISystemProcessTransactionSupervisorHierarchy>

  restrictFieldId?: Maybe<string>

  groupsSecurityAccess?: Maybe<Maybe<IGroupsSecurityAccess>[]>

  position?: Maybe<number>

  relatedFormAccessList?: Maybe<Maybe<IRelatedFormAccessList>[]>

  createRelatedForm?: Maybe<ICreateRelatedForm>
}

export interface IRecipientField {
  fieldId?: Maybe<string>
  fieldLinkId?: Maybe<string>
}

export interface ISystemProcessTransactionRecipient {
  sendTo?: Maybe<SystemProcessTransactionSendTo>

  sendToUsers?: Maybe<Maybe<string>[]>

  sendToPositions?: Maybe<Maybe<string>[]>

  sendToFieldIds?: Maybe<Maybe<string>[]>

  sendToPreviousFieldIds?: Maybe<Maybe<string>[]>

  sendToParentReportsToPersonByFieldIds?: Maybe<Maybe<string>[]>

  sendToParentReportsToPositionByFieldIds?: Maybe<Maybe<string>[]>

  sendToParentPreviousFieldIds?: Maybe<Maybe<string>[]>

  sendToParentFieldIds?: Maybe<Maybe<string>[]>

  sendToReportsToPersonByFieldIds?: Maybe<Maybe<string>[]>

  sendToReportsToPositionByFieldIds?: Maybe<Maybe<string>[]>

  sendToFields?: Maybe<Maybe<IRecipientField>[]>

  sendToParentFields?: Maybe<Maybe<IRecipientField>[]>

  sendToReportsToPersonByFields?: Maybe<Maybe<IRecipientField>[]>

  sendToReportsToPositionByFields?: Maybe<Maybe<IRecipientField>[]>

  sendToPreviousFields?: Maybe<Maybe<IRecipientField>[]>
}

export interface ISystemProcessRelatedForm {
  riskTemplateId?: Maybe<string>

  multiple?: Maybe<boolean>

  position?: Maybe<number>

  inheritToManagers?: Maybe<boolean>

  businessRules?: Maybe<Maybe<ISystemProcessBusinessRule>[]>
}

export interface ISystemProcessBusinessRule {
  userOrPositionFieldId?: Maybe<string>

  securityProfileId?: Maybe<string>

  userOrPositionId?: Maybe<string>

  inheritSecurityProfileId?: Maybe<boolean>
}

export interface ISystemProcessTransactionRiskRecurrence {
  riskTemplateId?: Maybe<string>

  recurrence?: Maybe<Json>

  fieldValues?: Maybe<IFieldValueMapItem[]>
}

export interface ISystemProcessTransactionDisableRiskRecurrence {
  riskTemplateId?: Maybe<string>
}

export interface ISystemProcessTransactionEnableRiskRecurrence {
  riskTemplateId?: Maybe<string>
}

export interface ISystemProcessTransactionSupervisorHierarchy {
  fieldIds?: Maybe<string[]>
}

export interface IGroupsSecurityAccess {
  userId?: Maybe<string>

  positionId?: Maybe<string>

  securityProfileId?: Maybe<string>
}

export interface ILinkingItemsResponse {
  pagination: IPagination

  data: Maybe<ILinkingItem>[]
}

export interface ILocationsResponse {
  pagination: IPagination

  data: Maybe<ILocation>[]
}

export interface ILocation {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  parentId?: Maybe<string>

  active?: Maybe<boolean>

  expanded?: Maybe<boolean>

  position?: Maybe<number>

  description?: Maybe<string>

  code?: Maybe<string>

  parent?: Maybe<ILocation>

  geoLocation?: Maybe<IGeoLocation>
}

export interface IGeoLocation {
  city?: Maybe<string>

  state?: Maybe<string>

  address?: Maybe<string>

  country?: Maybe<string>

  latitude?: Maybe<number>

  longitude?: Maybe<number>
}

export interface ILogsResponse {
  pagination: IPagination

  data: Maybe<ILog>[]
}

export interface IObjectEntitiesResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IObjectEntity>[]>
}

export interface IObjectSecuritiesResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IObjectSecurity>[]>
}

export interface IPlansResponse {
  pagination: IPagination

  data: Maybe<IPlan>[]
}

export interface IHomePageResponse {
  data: IHomePageData
}

export interface IHomePageData {
  plans: IHomePagePlansData

  actions: Maybe<IPlanAction>[]

  kpis: Maybe<IPlanKpi>[]
}

export interface IHomePagePlansData {
  data: Maybe<IPlan>[]

  pagination: IPagination
}

export interface IPlanActionsResponse {
  pagination: IPagination

  data: Maybe<IPlanAction>[]
}

export interface IPlanKpIsResponse {
  pagination: IPagination

  data: Maybe<IPlanKpi>[]
}

export interface IPlanObjectivesResponse {
  pagination: IPagination

  data: Maybe<IPlanObjective>[]
}

export interface IPlanObjectiveResourcesResponse {
  pagination: IPagination

  data: Maybe<IPlanObjectiveResource>[]
}

export interface IPlanTypesResponse {
  pagination: IPagination

  data: Maybe<IPlanType>[]
}

export interface IPositionsResponse {
  pagination: IPagination

  data: Maybe<IPosition>[]
}

export interface IPowerBiWorkspacesResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IPowerBiWorkspace>[]>
}

export interface IPowerBiWorkspace {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  isReadOnly?: Maybe<boolean>

  isOnDedicatedCapacity?: Maybe<boolean>

  name?: Maybe<string>

  standard?: Maybe<boolean>
}

export interface IPowerBiReportsResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IPowerBiReport>[]>
}

export interface IPowerBiReportsResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IPowerBiReport>[]>
}

export interface IReportsAvailableTo {
  availableToUserOrPositionIds?: Maybe<Array<string>>
}

export interface IPowerBiReport {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  workspaceId?: Maybe<string>

  datasetId?: Maybe<string>

  name?: Maybe<string>

  customName?: Maybe<string>

  webUrl?: Maybe<string>

  embedUrl?: Maybe<string>

  standard?: Maybe<boolean>

  availableTo?: Maybe<IReportsAvailableTo>
}

export interface IPowerBiDatasetRefreshedAtResponse {
  data?: Maybe<IPowerBiDatasetRefreshedAtData>
}

export interface IPowerBiDatasetRefreshedAtData {
  time?: Maybe<DateString>

  status?: Maybe<PowerBiDatasetRefreshStatus>
}

export interface IPowerBiCheckExportToFileStatusResponse {
  data?: Maybe<IPowerBiCheckExportToFileStatusData>
}

export interface IPowerBiCheckExportToFileStatusData {
  exportId: string

  status: string
}

export interface IPowerBiStandardReportsResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<IPowerBiStandardReport>[]>
}

export interface IPowerBiStandardReport {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  name?: Maybe<string>

  workspaceId?: Maybe<string>
}

export interface IRisksResponse {
  pagination: IRisksPagination

  data: Maybe<IRisk>[]
}

export interface IRisksPagination {
  pageTake: number

  pageSkip: number

  totalCount?: Maybe<number>

  isInfiniteLoader?: Maybe<boolean>
}

export interface IInlineRelatedForm {
  id?: Maybe<string>

  riskTemplateId: Maybe<string>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>
}

export interface IRiskCustomColumn {
  label: string
  fieldValue: IFieldValue
}

export interface IRisk {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  startOfTheNextPeriodAt?: Maybe<DateString>

  recurrence?: Maybe<Json>

  parentRiskId?: Maybe<string>

  parentRisk?: Maybe<IRisk>

  recurrenceFromRiskId?: Maybe<string>

  archived?: Maybe<boolean>

  isGuestAccess?: Maybe<boolean>

  private?: Maybe<boolean>

  riskTemplate?: Maybe<IRiskTemplate>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  permissions?: Maybe<ISecurityProfilePermissions>

  customerUniqueIdPostfix?: Maybe<string>

  canUpdateFromSystemProcesses?: Maybe<boolean>

  canDeleteFromSystemProcesses?: Maybe<boolean>

  isCreatedFromGuestAccess?: Maybe<boolean>

  realRiskId?: Maybe<string>

  inlineRelatedForms?: Maybe<Maybe<IInlineRelatedForm>[]>

  customColumns?: Maybe<Maybe<IRiskCustomColumn>[]>

  mainRisk?: Maybe<IRisk>

  createdBy?: Maybe<IUser>

  updatedBy?: Maybe<IUser>
}

export interface IRiskTemplate {
  id?: Maybe<string>

  companyId?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  fields?: Maybe<Maybe<IField>[]>

  fieldLinks?: Maybe<Maybe<IFieldLink>[]>

  fieldGroups?: Maybe<Maybe<IFieldGroup>[]>

  archived?: Maybe<boolean>

  name?: Maybe<string>

  private?: Maybe<boolean>

  isGuestAccess?: Maybe<boolean>

  isPublicSearchable?: Maybe<boolean>

  fieldValueMap?: Maybe<Maybe<IFieldValueMapItem>[]>

  permissions?: Maybe<ISecurityProfilePermissions>

  relatedRiskTemplatesIdsFromSystemProcesses?: Maybe<string[]>

  generateCustomerUniqueId?: Maybe<boolean>

  customerUniqueIdPrefix?: Maybe<string>

  includeInAllAiContext?: Maybe<boolean>

  aiEnabled?: Maybe<boolean>

  aiContextDescription?: Maybe<string>

  archiveOnDelete?: Maybe<boolean>

  aiSuggestedPrompts?: Maybe<string[]>

  formConditions?: Maybe<IRiskTemplateFormCondition[]>
}

export interface IFieldLink {
  id?: Maybe<string>

  fieldId?: Maybe<string>
  /** current risk template */
  targetEntityId?: Maybe<string>
  /** parent may be table */
  parentFieldId?: Maybe<string>

  fieldGroups?: Maybe<Maybe<IFieldGroup>[]>

  field?: Maybe<IField>

  addFieldLabel?: Maybe<string>

  fieldLabel?: Maybe<string>

  position?: Maybe<number>

  primary?: Maybe<boolean>

  riskViewId?: Maybe<string>

  linkingSettings?: Maybe<IFieldLinkSettings>
}

export interface IFieldGroup {
  id?: Maybe<string>

  fieldId?: Maybe<string>
  /** risk template current */
  entityId?: Maybe<string>
  /** table id */
  parentFieldId?: Maybe<string>
  /** risk template */
  groupedByEntityId?: Maybe<string>
}

export interface IRiskTemplateFormCondition {
  id?: Maybe<string>

  fieldId?: Maybe<string>

  trigger?: Maybe<IRiskTemplateFormConditionTrigger>

  actions?: Maybe<IRiskTemplateFormConditionAction[]>
}

export interface IRiskTemplateFormConditionTrigger {
  value?: Maybe<Json>
}

export interface IRiskTemplateFormConditionAction {
  type?: Maybe<RiskTemplateFormConditionActionType>

  fieldId?: Maybe<string>

  value?: Maybe<Json>
}

export interface IGetRelatedFormsPermissionsResponse {
  permissionsMap?: Maybe<IPermissionsMap[]>

  riskTemplates?: Maybe<IRiskTemplate[]>
}

export interface IPermissionsMap {
  riskId?: Maybe<string>

  risk: Maybe<IRisk>

  riskTemplateId: string

  permissions?: Maybe<ISecurityProfilePermissions>
}

export interface IGetGroupedFormsPermissionsResponse {
  groupedFormsPermissionsMap: IGroupedFormsPermissionsMap[]
}

export interface ILinkedFormsPermissionsMap {
  permissionsMap?: Maybe<IPermissionsMap[]>
}

export interface IGetLinkedFormsPermissionsArgs {
  riskId: string
}

export interface IGroupedFormsPermissionsMap {
  primaryFieldValueId: string

  permissions: ISecurityProfilePermissions
}

export interface IFieldValue {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  entityId?: Maybe<string>

  fieldId?: Maybe<string>

  value?: Maybe<Json>

  row?: Maybe<number>

  column?: Maybe<number>

  parentFieldValueId?: Maybe<string>
  /** temporary */
  fieldLink?: Maybe<IFieldLink>

  field?: Maybe<IField>

  linkedFieldValue?: Maybe<IFieldValueMapItem>
}

export interface IRiskTemplatesResponse {
  pagination: IPagination

  data: Maybe<IRiskTemplate>[]
}

export interface IRiskViewsResponse {
  pagination: IPagination

  data: Maybe<IRiskView>[]
}

export type CustomColumnSource = {
  fieldId: string
  templateId: string
}

export interface CustomColumn {
  id: string
  name: string
  sources: CustomColumnSource[]
}

export interface IRiskView {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  name?: Maybe<string>

  riskTemplate?: Maybe<IRiskTemplate>

  settings?: Maybe<IRiskViewSettings>

  customColumns?: Maybe<CustomColumn[]>

  isLinkedView?: Maybe<boolean>

  availableToUserOrPositionIds?: Maybe<string[]>
}

export interface IRiskViewSettings {
  fields?: Maybe<Maybe<IRiskViewSettingsItem>[]>
}

export interface IRiskViewSettingsItem {
  fieldId?: Maybe<string>

  active?: Maybe<boolean>

  parentFieldId?: Maybe<string>

  fieldLinkId?: Maybe<string>

  riskTemplateId?: Maybe<string>
}

export interface ISecurityGroupsResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<ISecurityGroup>[]>
}

export interface IPrmissionsFromSecurityGroupsResponse {
  data?: Maybe<ISecurityProfilePermissions>
}

export interface ISecurityProfilesResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<ISecurityProfile>[]>
}

export interface ISystemProcessesResponse {
  pagination?: Maybe<IPagination>

  data?: Maybe<Maybe<ISystemProcess>[]>
}

export interface ISystemProcess {
  id?: Maybe<string>

  createdAt?: Maybe<DateString>

  updatedAt?: Maybe<DateString>

  companyId?: Maybe<string>

  active?: Maybe<boolean>

  name?: Maybe<string>

  processType?: Maybe<SystemProcessType>

  object?: Maybe<SystemProcessObject>

  conditions?: Maybe<Maybe<ISystemProcessCondition>[]>

  transactions?: Maybe<Maybe<ISystemProcessTransaction>[]>
}

export interface IInlineFunction {
  id: string

  transactionId?: Maybe<string>

  functionName?: Maybe<string>
}

export interface IUserResponse extends IIResponse {
  ok: boolean

  message?: Maybe<string>

  data?: Maybe<IUser>
}

export interface IUsersResponse {
  pagination: IPagination

  data: Maybe<IUser>[]
}

export interface IUsersOrPositionsResponse {
  data?: Maybe<IUserOrPosition[]>

  pagination?: Maybe<IPagination>
}

export interface IMutation {
  _?: Maybe<boolean>

  apiTokenCreate?: Maybe<IApiTokenResponse>

  apiTokenDelete?: Maybe<IMutationResponse>

  generateUploadUrls?: Maybe<IGenerateUploadUrlsResponse>

  authSignIn?: Maybe<IAuthUserWithCredentialsResponse>

  authRefreshToken?: Maybe<IAuthResponse>

  authResetPassword?: Maybe<IMutationResponse>

  authSetNewPassword?: Maybe<IAuthUserWithCredentialsResponse>

  authMicrosoftTokenGetAvailableCompanies?: Maybe<IAuthMicrosoftTokenGetAvailableCompaniesResponse>

  authExchangeMicrosoftToken?: Maybe<IAuthUserWithCredentialsResponse>

  bookmarkCreate?: Maybe<IMutationResponse>

  bookmarkDelete?: Maybe<IMutationResponse>

  businessActivityUpdate?: Maybe<IMutationResponse>

  businessActivityCreate?: Maybe<IMutationResponse>

  businessActivitiesTreeUpdate?: Maybe<IMutationResponse>

  businessActivityDelete?: Maybe<IMutationResponse>

  businessProcessUpdate?: Maybe<IMutationResponse>

  businessProcessCreate?: Maybe<IMutationResponse>

  businessProcessesTreeUpdate?: Maybe<IMutationResponse>

  businessProcessDelete?: Maybe<IMutationResponse>

  businessUnitUpdate?: Maybe<IMutationResponse>

  businessUnitCreate?: Maybe<IMutationResponse>

  businessUnitsTreeUpdate?: Maybe<IMutationResponse>

  businessUnitDelete?: Maybe<IMutationResponse>

  companyCreate?: Maybe<IMutationResponse>

  companyUpdate?: Maybe<IMutationResponse>

  companyUpdateRiskResponseTypes?: Maybe<IMutationResponse>

  dashboardCreate?: Maybe<IDashboard>

  dashboardUpdate?: Maybe<IDashboard>

  dashboardDelete?: Maybe<IMutationResponse>

  dashboardItemCreate?: Maybe<IDashboardItem>

  dashboardItemUpdate?: Maybe<IDashboardItem>

  dashboardItemDelete?: Maybe<IMutationResponse>

  dummyMutation?: Maybe<IMutationResponse>

  fieldCreate?: Maybe<IFieldResponse>

  fieldUpdate?: Maybe<IFieldResponse>

  fieldUpdatePositions?: Maybe<IMutationResponse>

  fieldDelete?: Maybe<IMutationResponse>

  fieldGroupBunchCreate?: Maybe<IFieldGroupBunchResponse>

  fieldGroupBunchDelete?: Maybe<IMutationResponse>

  fieldLinkCreate?: Maybe<IFieldLinkResponse>

  fieldLinkUpdate?: Maybe<IFieldLinkResponse>

  fieldLinkDelete?: Maybe<IMutationResponse>

  fieldLinkBunchUpdate?: Maybe<IMutationResponse>

  fieldLinkBunchDelete?: Maybe<IMutationResponse>

  fieldValueUpsert?: Maybe<Maybe<IFieldValue>[]>

  fieldValueUpsertPublic?: Maybe<Maybe<IFieldValue>[]>

  fieldValueDelete?: Maybe<IMutationResponse>

  fieldValueDeletePublic?: Maybe<IMutationResponse>

  filterCreate?: Maybe<IFilter>

  filterUpdate?: Maybe<IFilter>

  filterDelete?: Maybe<IMutationResponse>

  activeDirectoryProcessAdminCode?: Maybe<IMutationResponse>

  activeDirectorySync?: Maybe<IMutationResponse>

  activeDirectoryDelete?: Maybe<IMutationResponse>

  tokenUpdate?: Maybe<IMutationResponse>

  linkingItemCreate?: Maybe<ILinkingItemResponse>

  linkingItemUpdate?: Maybe<ILinkingItemResponse>

  linkingItemDelete?: Maybe<IMutationResponse>

  locationUpdate?: Maybe<IMutationResponse>

  locationCreate?: Maybe<IMutationResponse>

  locationsTreeUpdate?: Maybe<IMutationResponse>

  locationDelete?: Maybe<IMutationResponse>

  logCreate?: Maybe<IMutationResponse>

  logUpdate?: Maybe<IMutationResponse>

  logDelete?: Maybe<IMutationResponse>

  acknowledgeNotification?: Maybe<IMutationResponse>

  acknowledgeNotificationPublic?: Maybe<IMutationResponse>

  objectEntityUpdate?: Maybe<IObjectEntityResponse>

  objectSecurityCreate?: Maybe<IObjectSecurityResponse>

  objectSecurityUpdate?: Maybe<IObjectSecurityResponse>

  objectSecurityDelete?: Maybe<IMutationResponse>

  planUpdateTree?: Maybe<IMutationResponse>

  planCreate?: Maybe<IPlanCreateResponse>

  planUpdate?: Maybe<IMutationResponse>

  planDelete?: Maybe<IMutationResponse>

  planBulkItemsUpdate?: Maybe<IMutationResponse>

  planActionCreate?: Maybe<IPlanActionResponse>

  planActionUpdate?: Maybe<IPlanActionResponse>

  planActionDelete?: Maybe<IMutationResponse>

  planItemCreate?: Maybe<IPlanItemResponse>

  planItemDelete?: Maybe<IPlanItemResponse>

  planItemDeleteBulkReferenced?: Maybe<IMutationResponse>

  planKPICreate?: Maybe<IPlanKpiResponse>

  planKPIUpdate?: Maybe<IPlanKpiResponse>

  planKPIDelete?: Maybe<IMutationResponse>

  planKPIScaleOptionCreate?: Maybe<IPlanKpiScaleOptionResponse>

  planKPIScaleOptionCUD: IMutationResponse

  planObjectiveCreate?: Maybe<IPlanObjectiveResponse>

  planObjectiveUpdate?: Maybe<IPlanObjectiveResponse>

  planObjectiveDelete?: Maybe<IMutationResponse>

  planObjectiveResourceCreate?: Maybe<IMutationResponse>

  planObjectiveResourceUpdate?: Maybe<IMutationResponse>

  planObjectiveResourceDelete?: Maybe<IMutationResponse>

  planTypeCreate?: Maybe<IMutationResponse>

  planTypeUpdate?: Maybe<IMutationResponse>

  planTypeDelete?: Maybe<IMutationResponse>

  positionUpdate?: Maybe<IMutationResponse>

  positionCreate?: Maybe<IMutationResponse>

  positionsTreeUpdate?: Maybe<IMutationResponse>

  positionDelete?: Maybe<IMutationResponse>

  powerBIReportCreate?: Maybe<IPowerBiTokenResponse>

  powerBIReportUpdate?: Maybe<IPowerBiReportResponse>

  powerBIReportClone?: Maybe<IPowerBiReportResponse>

  powerBIReportDelete?: Maybe<IPowerBiTokenResponse>

  powerBIReportCreateCallback?: Maybe<IMutationResponse>

  powerBITokenCreate?: Maybe<IPowerBiTokenResponse>

  powerBIRefreshDatasets?: Maybe<IMutationResponse>

  powerBIStartExportToFile: IPowerBiStartExportToFileResponse

  powerBIUpdateStandardReport: IMutationResponse

  reviewCreate?: Maybe<IReviewResponse>

  reviewUpdate?: Maybe<IMutationResponse>

  reviewDelete?: Maybe<IMutationResponse>

  riskCreate?: Maybe<IRiskResponse>

  riskCreatePublic?: Maybe<IRiskResponse>

  riskUpdate?: Maybe<IRiskResponse>

  riskDelete?: Maybe<IMutationResponse>

  riskRemoveGuestAccessPublic?: Maybe<IMutationResponse>

  riskTemplateCreate?: Maybe<IRiskTemplateCreateResponse>

  riskTemplateUpdate?: Maybe<IMutationResponse>

  riskTemplateDelete?: Maybe<IMutationResponse>

  riskViewCreate?: Maybe<IRiskViewResponse>

  riskViewUpdate?: Maybe<IMutationResponse>

  riskViewDelete?: Maybe<IMutationResponse>

  scheduledReviewCreate?: Maybe<IScheduledReviewResponse>

  scheduledReviewUpdate?: Maybe<IMutationResponse>

  scheduledReviewDelete?: Maybe<IMutationResponse>

  scheduledReviewCopyWithReview?: Maybe<IScheduledReviewResponse>

  securityGroupCreate?: Maybe<ISecurityGroupResponse>

  securityGroupUpdate?: Maybe<ISecurityGroupResponse>

  securityGroupDelete?: Maybe<IMutationResponse>

  securityProfileCreate?: Maybe<ISecurityProfileResponse>

  securityProfileUpdate?: Maybe<ISecurityProfileResponse>

  securityProfileDelete?: Maybe<IMutationResponse>

  previewEmail?: Maybe<IPreviewEmailResponse>

  runAutomations?: Maybe<IMutationResponse>

  systemProcessCreate?: Maybe<ISystemProcessResponse>

  systemProcessUpdate?: Maybe<ISystemProcessResponse>

  systemProcessDelete?: Maybe<IMutationResponse>

  executeCustomFunctionTransaction?: Maybe<IMutationResponse>

  executeCustomFunctionTransactionPublic?: Maybe<IMutationResponse>

  checkAndNotifyInlineFunctionVisibility?: Maybe<IMutationResponse>

  checkAndNotifyInlineFunctionVisibilityPublic?: Maybe<IMutationResponse>

  executeInlineFunctionTransaction?: Maybe<IMutationResponse>

  executeInlineFunctionTransactionPublic?: Maybe<IMutationResponse>

  userCreate?: Maybe<IMutationResponse>

  userUpdate?: Maybe<IMutationResponse>
}

export interface IApiTokenResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IApiToken>
}

export interface IMutationResponse extends IIResponse {
  ok: boolean

  message?: Maybe<string>
}

export interface IGenerateUploadUrlsResponse {
  downloadUrl?: Maybe<string>

  uploadUrl?: Maybe<string>

  uploadFormData?: Maybe<FormData>
}

export interface IAuthUserWithCredentialsResponse extends IIResponse {
  ok: boolean

  message?: Maybe<string>

  data?: Maybe<IAuthUserWithToken>
}

export interface IAuthUserWithToken {
  accessToken: string

  user?: Maybe<IUser>

  twoFactorRequired: boolean

  twoFactorToken: string

  twoFactorSetupRequired?: boolean

  twoFactorSetupToken?: string
}

export interface IAuthResponse extends IIResponse {
  ok: boolean

  message?: Maybe<string>

  data?: Maybe<IAuthUserWithToken>
}

export interface IAuthMicrosoftTokenGetAvailableCompaniesResponse {
  data: ICompany[]
}

export interface IFieldResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IField>
}

export interface IFieldGroupBunchResponse {
  ok?: Maybe<boolean>

  data?: Maybe<Maybe<IFieldGroup>[]>
}

export interface IFieldLinkResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IFieldLink>
}

export interface ILinkingItemResponse {
  data?: Maybe<ILinkingItem>
}

export interface IObjectEntityResponse {
  data?: Maybe<IObjectEntity>
}

export interface IObjectSecurityResponse {
  data?: Maybe<IObjectSecurity>
}

export interface IPlanCreateResponse {
  ok: boolean

  data?: Maybe<IPlan>
}

export interface IPlanActionResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IPlanAction>
}

export interface IPlanItemResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IPlanItem>
}

export interface IPlanKpiResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IPlanKpi>
}

export interface IPlanKpiScaleOptionResponse {
  ok: boolean

  data?: Maybe<IPlanKpiScaleOption>
}

export interface IPlanObjectiveResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IPlanObjective>
}

export interface IPowerBiTokenResponse {
  data?: Maybe<IPowerBiToken>
}

export interface IPowerBiToken {
  token?: Maybe<string>

  expiration?: Maybe<DateString>
}

export interface IPowerBiReportResponse {
  data?: Maybe<IPowerBiReport>
}

export interface IPowerBiStartExportToFileResponse {
  data: IPowerBiStartExportToFileData
}

export interface IPowerBiStartExportToFileData {
  exportId: string
}

export interface IReviewResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IReview>
}

export interface IRiskResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IRisk>
}

export interface IRiskTemplateCreateResponse {
  ok: boolean

  data?: Maybe<IRiskTemplate>
}

export interface IRiskViewResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IRiskView>
}

export interface IScheduledReviewResponse {
  ok?: Maybe<boolean>

  data?: Maybe<IScheduledReview>
}

export interface ISecurityGroupResponse {
  data?: Maybe<ISecurityGroup>
}

export interface ISecurityProfileResponse {
  data?: Maybe<ISecurityProfile>
}

export interface IPreviewEmailResponse {
  subject?: Maybe<string>

  template?: Maybe<string>
}

export interface ISystemProcessResponse {
  data?: Maybe<ISystemProcess>
}

export interface ISubscription {
  notification: INotification
}

export interface INotification {
  id: string

  payload?: Maybe<Json>

  timestamp: DateString
}

export interface IDeleteResponse extends IIResponse {
  ok: boolean

  message?: Maybe<string>
}

export interface IGlobalGraphItem {
  id?: Maybe<string>
}

export interface IPlanKpiScaleOptionsResponse {
  ok: boolean

  data?: Maybe<Maybe<IPlanKpiScaleOption>[]>
}

export interface IPlanTheme {
  title?: Maybe<string>

  description?: Maybe<string>
}

export interface IPlanValue {
  title?: Maybe<string>

  description?: Maybe<string>
}

export interface IPowerBiStandardReportResponse {
  data?: Maybe<IPowerBiStandardReport>
}

// ====================================================
// Arguments
// ====================================================

export interface IApiTokensQueryArgs {
  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IBusinessActivitiesQueryArgs {
  filter?: Maybe<IBusinessActivityFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IBusinessProcessesQueryArgs {
  filter?: Maybe<IBusinessProcessFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IBusinessUnitsQueryArgs {
  filter?: Maybe<IBusinessUnitFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface ICompaniesQueryArgs {
  filter?: Maybe<ICompanyFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface ICompanyFieldsQueryArgs {
  filter?: Maybe<ICompanyFieldFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IDashboardItemsQueryArgs {
  input: IDashboardItemsInput
}
export interface IFiltersQueryArgs {
  filter?: Maybe<IFilterFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IGlobalSearchQueryArgs {
  filter: IGlobalSearchFilterInput

  pagination?: Maybe<IPaginationInput>
}
export interface IActiveDirectoriesQueryArgs {
  pagination: IPaginationInput
}
export interface ILinkingItemsQueryArgs {
  filter?: Maybe<ILinkingItemFilter>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface ILocationsQueryArgs {
  filter?: Maybe<ILocationFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface ILogsQueryArgs {
  filter?: Maybe<ILogFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IObjectEntitiesQueryArgs {
  filter?: Maybe<IObjectEntityFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IObjectSecuritiesQueryArgs {
  filter?: Maybe<IObjectSecurityFilter>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlansQueryArgs {
  filter?: Maybe<IPlanFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IHomePlansQueryArgs {
  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IPlanActionsQueryArgs {
  filter?: Maybe<IPlanActionFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanItemsQueryArgs {
  filter?: Maybe<IPlanItemFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanKpIsQueryArgs {
  filter?: Maybe<IPlanKpiFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanObjectivesQueryArgs {
  filter?: Maybe<IPlanObjectiveFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanResourcesQueryArgs {
  filter?: Maybe<IPlanResourcesFilterInput>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IPlanTypesQueryArgs {
  filter?: Maybe<IPlanTypeFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPositionsQueryArgs {
  filter?: Maybe<IPositionFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IPowerBiWorkspacesQueryArgs {
  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPowerBiReportsQueryArgs {
  filter?: Maybe<IPowerBiReportsFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPowerBiCheckExportToFileStatusQueryArgs {
  input: IPowerBiCheckExportToFileStatusInput
}
export interface IPowerBiStandardReportsQueryArgs {
  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IRisksQueryArgs {
  filter?: Maybe<IRiskFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IRisksPublicQueryArgs {
  filter?: Maybe<IRiskFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>

  domain: string
}
export interface IGetRelatedFormsPermissionsQueryArgs {
  riskId: string

  riskTemplateId: string
}
export interface IGetGroupedFormsPermissionsQueryArgs {
  riskId: string
}
export interface IGetDataForExportRiskQueryArgs {
  id: string
}
export interface IRiskTemplatesQueryArgs {
  filter?: Maybe<IRiskTemplateFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IRiskTemplatesPublicQueryArgs {
  filter?: Maybe<IRiskTemplateFilterInput>

  pagination?: Maybe<IPaginationInput>

  domain: string
}
export interface IRiskViewsQueryArgs {
  filter?: Maybe<IRiskViewFilterInput>

  pagination?: Maybe<IPaginationInput>
}
export interface ISecurityGroupsQueryArgs {
  filter?: Maybe<ISecurityGroupFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface ISecurityProfilesQueryArgs {
  filter?: Maybe<ISecurityProfileFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface ISystemProcessesQueryArgs {
  filter?: Maybe<ISystemProcessFilter>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface ISystemProcessesPublicQueryArgs {
  filter?: Maybe<ISystemProcessFilter>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>

  domain: string
}
export interface IGetInlineFunctionsQueryArgs {
  entityId: string
}
export interface IUsersQueryArgs {
  filter?: Maybe<IUserFilter>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IMyTeamQueryArgs {
  filter?: Maybe<IUserFilter>

  order?: Maybe<IOrderInput>

  pagination?: Maybe<IPaginationInput>
}
export interface IUsersOrPositionsQueryArgs {
  filter?: Maybe<IUsersOrPositionsFilter>

  pagination?: Maybe<IPaginationInput>
}
export interface IPlanItemsPlanArgs {
  filter?: Maybe<IPlanItemFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanItemsPlanObjectiveArgs {
  filter?: Maybe<IPlanItemFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanItemsPlanActionArgs {
  filter?: Maybe<IPlanItemFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IPlanItemsPlanKpiArgs {
  filter?: Maybe<IPlanItemFilterInput>

  pagination?: Maybe<IPaginationInput>

  order?: Maybe<IOrderInput>
}
export interface IApiTokenDeleteMutationArgs {
  id: string
}
export interface IGenerateUploadUrlsMutationArgs {
  file: IFileInput
}
export interface IAuthSignInMutationArgs {
  email: string

  password: string

  domain: string
}
// authRefreshToken no longer takes arguments — server reads refresh token from HTTP-only cookie
export type IAuthRefreshTokenMutationArgs = Record<string, never>
export interface IAuthResetPasswordMutationArgs {
  domain: string

  email: string
}
export interface IAuthSetNewPasswordMutationArgs {
  domain: string

  email: string

  token: string

  password: string
}
export interface IAuthMicrosoftTokenGetAvailableCompaniesMutationArgs {
  token: string
}
export interface IAuthExchangeMicrosoftTokenMutationArgs {
  token: string

  companyId: string
}
export interface IBookmarkCreateMutationArgs {
  input?: Maybe<IBookmarkCreateInput>
}
export interface IBookmarkDeleteMutationArgs {
  input?: Maybe<IBookmarkCreateInput>
}
export interface IBusinessActivityUpdateMutationArgs {
  id: string

  businessActivity: IBusinessActivityUpdateInput
}
export interface IBusinessActivityCreateMutationArgs {
  businessActivity: IBusinessActivityCreateInput
}
export interface IBusinessActivitiesTreeUpdateMutationArgs {
  businessActivities: Maybe<IBusinessActivitiesTreeUpdateInput>[]
}
export interface IBusinessActivityDeleteMutationArgs {
  id: string
}
export interface IBusinessProcessUpdateMutationArgs {
  id: string

  businessProcess: IBusinessProcessUpdateInput
}
export interface IBusinessProcessCreateMutationArgs {
  businessProcess: IBusinessProcessCreateInput
}
export interface IBusinessProcessesTreeUpdateMutationArgs {
  businessProcesses: Maybe<IBusinessProcessesTreeUpdateInput>[]
}
export interface IBusinessProcessDeleteMutationArgs {
  id: string
}
export interface IBusinessUnitUpdateMutationArgs {
  id: string

  businessUnit: IBusinessUnitUpdateInput
}
export interface IBusinessUnitCreateMutationArgs {
  businessUnit: IBusinessUnitCreateInput
}
export interface IBusinessUnitsTreeUpdateMutationArgs {
  businessUnits: Maybe<IBusinessUnitsTreeUpdateInput>[]
}
export interface IBusinessUnitDeleteMutationArgs {
  id: string
}
export interface ICompanyCreateMutationArgs {
  company: ICompanyCreateInput
}
export interface ICompanyUpdateMutationArgs {
  id: string

  company: ICompanyUpdateInput
}
export interface ICompanyUpdateRiskResponseTypesMutationArgs {
  id: string

  input: ICompanyUpdateRiskResponseTypesInput
}
export interface IDashboardCreateMutationArgs {
  input: IDashboardCreateInput
}
export interface IDashboardUpdateMutationArgs {
  input: IDashboardUpdateInput
}
export interface IDashboardDeleteMutationArgs {
  input: IDashboardDeleteInput
}
export interface IDashboardItemCreateMutationArgs {
  input: IDashboardItemCreateInput
}
export interface IDashboardItemUpdateMutationArgs {
  input: IDashboardItemUpdateInput
}
export interface IDashboardItemDeleteMutationArgs {
  input: IDashboardItemDeleteInput
}
export interface IFieldCreateMutationArgs {
  input: IFieldInput
}
export interface IFieldUpdateMutationArgs {
  id: string

  input: IFieldInput
}
export interface IFieldUpdatePositionsMutationArgs {
  input?: Maybe<Maybe<IFieldUpdatePositionInput>[]>
}
export interface IFieldDeleteMutationArgs {
  id: string

  forceDelete?: Maybe<boolean>
}
export interface IFieldGroupBunchCreateMutationArgs {
  input: IFieldGroupInput[]
}
export interface IFieldGroupBunchDeleteMutationArgs {
  input: IFieldGroupBunchDeleteInput[]
}

export interface IFieldValueSetArchivedMutationArgs {
  ids: string[]
  archived: boolean
}

export interface IFieldLinkCreateMutationArgs {
  input: IFieldLinkInput
}
export interface IFieldLinkUpdateMutationArgs {
  id: string

  input: IFieldLinkInput
}
export interface IFieldLinkDeleteMutationArgs {
  id: string
}
export interface IFieldLinkBunchUpdateMutationArgs {
  input: IFieldLinkInput[]
}
export interface IFieldLinkBunchDeleteMutationArgs {
  input: IFieldLinkBunchDeleteInput[]
}
export interface IFieldValueUpsertMutationArgs {
  entityId: string

  input: IFieldValueInput[]
}
export interface IFieldValueUpsertPublicMutationArgs {
  entityId: string

  input: IFieldValueInput[]

  domain: string
}
export interface IFieldValueDeleteMutationArgs {
  ids: string[]
}
export interface IFieldValueDeletePublicMutationArgs {
  ids: string[]

  domain: string
}
export interface IFilterCreateMutationArgs {
  input: IFilterCreateInput
}
export interface IFilterUpdateMutationArgs {
  input: IFilterUpdateInput
}
export interface IFilterDeleteMutationArgs {
  input: IFilterDeleteInput
}
export interface IActiveDirectoryProcessAdminCodeMutationArgs {
  code: string
}
export interface IActiveDirectoryDeleteMutationArgs {
  input: IActiveDirectoryDeleteInput
}
export interface ITokenUpdateMutationArgs {
  input: ITokenUpdateInput
}
export interface ILinkingItemCreateMutationArgs {
  input: ILinkingItemCreateInput
}
export interface ILinkingItemUpdateMutationArgs {
  id: string

  input: ILinkingItemUpdateInput
}
export interface ILinkingItemDeleteMutationArgs {
  id: string
}
export interface ILocationUpdateMutationArgs {
  id: string

  location: ILocationUpdateInput
}
export interface ILocationCreateMutationArgs {
  location: ILocationCreateInput
}
export interface ILocationsTreeUpdateMutationArgs {
  locations: Maybe<ILocationsTreeUpdateInput>[]
}
export interface ILocationDeleteMutationArgs {
  id: string
}
export interface ILogCreateMutationArgs {
  input?: Maybe<ILogCreateInput>
}
export interface ILogUpdateMutationArgs {
  id: string

  input?: Maybe<ILogUpdateInput>
}
export interface ILogDeleteMutationArgs {
  id: string
}
export interface IAcknowledgeNotificationMutationArgs {
  id: string
}
export interface IAcknowledgeNotificationPublicMutationArgs {
  id: string

  domain: string
}
export interface IObjectEntityUpdateMutationArgs {
  id: string

  input: IObjectEntityUpdateInput
}
export interface IObjectSecurityCreateMutationArgs {
  companyId?: Maybe<string>

  input: IObjectSecurityInput
}
export interface IObjectSecurityUpdateMutationArgs {
  id: string

  companyId?: Maybe<string>

  input: IObjectSecurityInput
}
export interface IObjectSecurityDeleteMutationArgs {
  id: string

  companyId?: Maybe<string>
}
export interface IPlanUpdateTreeMutationArgs {
  input?: Maybe<Maybe<IPlanUpdateTreeInput>[]>
}
export interface IPlanCreateMutationArgs {
  input: IPlanCreateInput
}
export interface IPlanUpdateMutationArgs {
  id: string

  input: IPlanUpdateInput
}
export interface IPlanDeleteMutationArgs {
  id: string

  input?: Maybe<IPlanDeleteInput>
}
export interface IPlanBulkItemsUpdateMutationArgs {
  id: string

  input: Json
}
export interface IPlanActionCreateMutationArgs {
  input: IPlanActionCreateInput
}
export interface IPlanActionUpdateMutationArgs {
  id: string

  input: IPlanActionUpdateInput
}
export interface IPlanActionDeleteMutationArgs {
  id: string
}
export interface IPlanItemCreateMutationArgs {
  input: IPlanItemCreateInput
}
export interface IPlanItemDeleteMutationArgs {
  id: string

  deleteSource?: Maybe<boolean>

  checkChildren?: Maybe<boolean>
}
export interface IPlanItemDeleteBulkReferencedMutationArgs {
  ids: Maybe<string>[]
}
export interface IPlanKpiCreateMutationArgs {
  input: IPlanKpiCreateInput
}
export interface IPlanKpiUpdateMutationArgs {
  id: string

  input: IPlanKpiUpdateInput
}
export interface IPlanKpiDeleteMutationArgs {
  id: string
}
export interface IPlanKpiScaleOptionCreateMutationArgs {
  input: IPlanKpiScaleOptionInput
}
export interface IPlanKpiScaleOptionCudMutationArgs {
  planKPIId: string

  input?: Maybe<Maybe<IPlanKpiScaleOptionCudInput>[]>
}
export interface IPlanObjectiveCreateMutationArgs {
  input: IPlanObjectiveCreateInput
}
export interface IPlanObjectiveUpdateMutationArgs {
  id: string

  input: IPlanObjectiveUpdateInput
}
export interface IPlanObjectiveDeleteMutationArgs {
  id: string
}
export interface IPlanObjectiveResourceCreateMutationArgs {
  input: IPlanObjectiveResourceInput
}
export interface IPlanObjectiveResourceUpdateMutationArgs {
  id: string

  input: IPlanObjectiveResourceInput
}
export interface IPlanObjectiveResourceDeleteMutationArgs {
  id: string
}
export interface IPlanTypeCreateMutationArgs {
  planType: IPlanTypeCreateInput
}
export interface IPlanTypeUpdateMutationArgs {
  id: string

  planType: IPlanTypeUpdateInput
}
export interface IPlanTypeDeleteMutationArgs {
  id: string
}
export interface IPositionUpdateMutationArgs {
  id: string

  position: IPositionUpdateInput
}
export interface IPositionCreateMutationArgs {
  position: IPositionCreateInput
}
export interface IPositionsTreeUpdateMutationArgs {
  positions: Maybe<IPositionsTreeUpdateInput>[]
}
export interface IPositionDeleteMutationArgs {
  id: string
}
export interface IPowerBiReportCreateMutationArgs {
  input: IPowerBiReportCreateInput
}
export interface IPowerBiReportUpdateMutationArgs {
  id: string

  input: IPowerBiReportUpdateInput
}
export interface IPowerBiReportCloneMutationArgs {
  input: IPowerBiReportCloneInput
}
export interface IPowerBiReportDeleteMutationArgs {
  id: string
}
export interface IPowerBiReportCreateCallbackMutationArgs {
  input: IPowerBiReportCreateCallbackInput
}
export interface IPowerBiTokenCreateMutationArgs {
  input: IPowerBiTokenCreateInput
}
export interface IPowerBiStartExportToFileMutationArgs {
  input: IPowerBiStartExportToFileInput
}
export interface IPowerBiUpdateStandardReportMutationArgs {
  input: IPowerBiUpdateStandardReportInput
}
export interface IReviewCreateMutationArgs {
  input: IReviewInput
}
export interface IReviewUpdateMutationArgs {
  id: string

  input: IReviewInput
}
export interface IReviewDeleteMutationArgs {
  id: string
}
export interface IRiskCreateMutationArgs {
  input: IRiskCreateInput
}
export interface IRiskCreatePublicMutationArgs {
  input: IRiskCreateInput

  domain: string
}
export interface IRiskUpdateMutationArgs {
  id: string

  input?: Maybe<IRiskUpdateInput>
}
export interface IRiskDeleteMutationArgs {
  id: string
}
export interface IRiskRemoveGuestAccessPublicMutationArgs {
  id: string

  domain: string
}
export interface IRiskTemplateCreateMutationArgs {
  input: IRiskTemplateCreateInput
}
export interface IRiskTemplateUpdateMutationArgs {
  id: string

  input: IRiskTemplateUpdateInput
}
export interface IRiskTemplateDeleteMutationArgs {
  id: string
}
export interface IRiskViewCreateMutationArgs {
  input: IRiskViewCreateInput
}
export interface IRiskViewUpdateMutationArgs {
  id: string

  input?: Maybe<IRiskViewUpdateInput>
}
export interface IRiskViewDeleteMutationArgs {
  id: string
}
export interface IScheduledReviewCreateMutationArgs {
  input: IScheduledReviewInput
}
export interface IScheduledReviewUpdateMutationArgs {
  id: string

  input: IScheduledReviewInput
}
export interface IScheduledReviewDeleteMutationArgs {
  id: string
}
export interface IScheduledReviewCopyWithReviewMutationArgs {
  reviewId?: Maybe<string>

  input: IScheduledReviewInput
}
export interface ISecurityGroupCreateMutationArgs {
  companyId?: Maybe<string>

  input: ISecurityGroupInput
}
export interface ISecurityGroupUpdateMutationArgs {
  id: string

  companyId?: Maybe<string>

  input: ISecurityGroupInput
}
export interface ISecurityGroupDeleteMutationArgs {
  id: string

  companyId?: Maybe<string>
}
export interface ISecurityProfileCreateMutationArgs {
  companyId?: Maybe<string>

  input: ISecurityProfileInput
}
export interface ISecurityProfileUpdateMutationArgs {
  id: string

  companyId?: Maybe<string>

  input: ISecurityProfileInput
}
export interface ISecurityProfileDeleteMutationArgs {
  id: string

  companyId?: Maybe<string>
}
export interface IPreviewEmailMutationArgs {
  input?: Maybe<IPreviewEmailInput>
}
export interface ISystemProcessCreateMutationArgs {
  input: ISystemProcessInput
}
export interface ISystemProcessUpdateMutationArgs {
  id: string

  input: ISystemProcessInput
}
export interface ISystemProcessDeleteMutationArgs {
  id: string
}
export interface IExecuteCustomFunctionTransactionMutationArgs {
  input: IExecuteCustomFunctionTransactionInput
}
export interface IExecuteCustomFunctionTransactionPublicMutationArgs {
  input: IExecuteCustomFunctionTransactionInput

  domain: string
}
export interface ICheckAndNotifyInlineFunctionVisibilityMutationArgs {
  input: ICheckAndNotifyInlineFunctionVisibilityInput
}
export interface ICheckAndNotifyInlineFunctionVisibilityPublicMutationArgs {
  input: ICheckAndNotifyInlineFunctionVisibilityInput

  domain: string
}
export interface IExecuteInlineFunctionTransactionMutationArgs {
  input: IExecuteInlineFunctionTransactionInput
}
export interface IExecuteInlineFunctionTransactionPublicMutationArgs {
  input: IExecuteInlineFunctionTransactionInput

  domain: string
}
export interface IUserCreateMutationArgs {
  user: IUserCreateInput
}
export interface IUserUpdateMutationArgs {
  id: string

  companyId: string

  user: IUserUpdateInput
}
export interface INotificationSubscriptionArgs {
  eventType: string
}

export interface IAuthVerifyTwoFactorResponse {
  authVerifyTwoFactor: IAuthUserWithCredentialsResponse
}

export interface ITwoFactorStatusData {
  enabled: boolean
  enabledAt: string
}

export interface ITwoFactorStatusResponse {
  data: ITwoFactorStatusData
  message: string
  ok: boolean
}

export interface IAuthTwoFactorStatus {
  authTwoFactorStatus: ITwoFactorStatusResponse
}

export interface ITwoFactorSetupData {
  backupCodes: string[]
  otpauthUri: string
}

export interface IAuthTwoFactorRegenerateBackupCodesResponse {
  authTwoFactorRegenerateBackupCodes: {
    data: ITwoFactorSetupData
    message: string
    ok: boolean
  }
}

// ====================================================
// Unions
// ====================================================

export type IUserOrPosition = IUser | IPosition
